import React, {useState, useMemo} from 'react'
import { useNavigate } from 'react-router'
import { useParams } from 'react-router-dom'
import useTitle from '@/hooks/useTitle'
import PulseLoader from "react-spinners/PulseLoader"
import { getTimeSince } from '@/utils/EtGetTimeSince' 
import { formatDateTime } from '@/utils/formatDate'
import EtContentEditorLight2 from "@/utils/EtContentEditorLight2"
import EtBtnFormBasic from "@/components/common/buttons/EtBtnFormBasic"
import { PlusIcon, MinusIcon, TrashIcon, PencilSquareIcon,ShareIcon  } from '@heroicons/react/24/outline'
import { userProfileIcon2 } from '@/assets/img/imgAssets'
import {
  RocketLaunchIcon,
  DocumentTextIcon,
  CursorArrowRippleIcon,
  Square3Stack3DIcon , ListBulletIcon, SwatchIcon, 
  ClockIcon, WrenchIcon,
  SunIcon,
  ArrowLeftCircleIcon, UserGroupIcon,
  ChartBarIcon, // 29 Oct 2026 - Coach Evaluations
} from "@heroicons/react/24/solid"
import { useGetUserSessionIntrospectionsQuery, useUpdateUserSessionIntrospectionMutation } from './userSessionIntrospectionsApiSlice'

import { useGetUserChallengeSelfEvalsQuery } from '../userChallengeSelfEvals/userChallengeSelfEvalsApiSlice' // 22 Oct 2024
import { useGetUserChallengeCoachEvalsQuery } from '../userChallengeCoachEvals/userChallengeCoachEvalsApiSlice' // 22 Oct 2024
import { useGetUserChallengeSponsorEvalsQuery } from '../userChallengeSponsorEvals/userChallengeSponsorEvalsApiSlice' // 22 Oct 2024

import EtUserActionListByGroupWithTabsComp from '../userActions/EtUserActionListByGroupWithTabsComp' // 23 Sep 2024
import EtModalCreateUserAction from '@/components/common/modal/EtModalCreateUserAction'

import EtModalCreateUserActionLanding from '@/components/common/modal/EtModalCreateUserActionLanding' // 17 May 2025

import Modal from '@/components/common/modal/Modal' // 05 Nov 2025
import ModalShareConfirm from '@/components/common/modal/ModalShareConfirm' // 05 Nov 2025

import { useGetUserPicQuery, useGetUsersQuery } from "../users/usersApiSlice" // 22 Oct 2024, 05 Nov 2025 - users query for building sharedWith array
import { useGetUserActionsQuery } from '../userActions/userActionsApiSlice' // 22 Oct 2024 - to filter and count user actions

// 13 Aug 2024 - Icons for Coach's Inputs
import EtCoachGroupIcon1SVGComp from '@/assets/img/icons/svgComp/EtCoachGroupIcon1SVGComp'
import EtCoachGroupIcon2SVGComp from '@/assets/img/icons/svgComp/EtCoachGroupIcon2SVGComp'
import EtCoachOneOnOneIcon2SVGComp from '@/assets/img/icons/svgComp/EtCoachOneOnOneIcon2SVGComp'

// 22 Oct 2024 - Icons for Sponsor Inputs
import EtSponsorGroupIcon1SVGComp from '@/assets/img/icons/svgComp/EtSponsorGroupIcon1SVGComp'
import EtSponsorOneOnOneIcon1SVGComp from '@/assets/img/icons/svgComp/EtSponsorOneOnOneIcon1SVGComp'
import EtChallengeIcon1SVGComp from '@/assets/img/icons/svgComp/EtChallengeIcon1SVGComp'
import EtModalAddOrUpdateUserChallengeSelfEval from '@/components/common/modal/EtModalAddOrUpdateUserChallengeSelfEval' 

import EtUserSessionQNABoard from '../userSessionQNAResponses/EtUserSessionQNABoard' // 26 May 2026

import { useGetOrgTrailSessionCohortsQuery } from '../orgTrailSessionCohorts/orgTrailSessionCohortsApiSlice' // 23 Jun 2026

// 30 Jul 2026
import {
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  Legend,
  Tooltip,
} from "recharts"

// 01 Aug 2026
import EtUserSessionChallengesSection from "../userChallengeSelfEvals/EtUserSessionChallengesSection"

// 20 Aug 2026 - User specific Session Challenge assignments
import {
  useGetUserSessionChallengeAssignmentsQuery,
} from "@/pages/admin/orgTrailSessionChallengeAssignments/orgTrailSessionChallengeAssignmentsApiSlice"

// 07 Oct 2026
import {
  useGetOrgTrailSessionQNAAssignmentsQuery,
} from "@/pages/admin/orgTrailSessionQNAAssignments/orgTrailSessionQNAAssignmentsApiSlice"

// 07 Oct 2026
import useAuth from '@/hooks/useAuth'

// 08 Oct 2026 - Session Reference Content
import {
  useGetMyAssignedSessionReferenceContentQuery,
} from "@/pages/admin/orgTrailSessionContentAssignments/orgTrailSessionContentAssignmentsApiSlice"

// 08 Oct 2026
import EtUserSessionReferenceContentBoard
  from "./EtUserSessionReferenceContentBoard"

// 29 Oct 2026 - Shared Coach Session Evaluations
import EtUserSessionCoachEvaluationsBoard
    from '@/features/userTrailSessions/EtUserSessionCoachEvaluationsBoard'

// 07 Oct 2026 - Resolve populated or plain reference IDs
const getRefId = (value) => {
  if (!value) {
    return ""
  }

  if (typeof value === "string") {
    return value
  }

  return value?._id || value?.id || ""
}




const EtUserTrailSessionIntrospectionCompProd = () => {

  // First Created - 13 Jun 2024
  // Author - Atul Pandey
  // Copied and refined from EtUserTrailSessionIntroduction
  // Updated - 14 Jun 2024 - Refined the notes section - to be added / edited separately
  // Updated - 12 Aug 2024 - Added Coach notes - got data from CoachOrgTrailSessionPadsQuery
  // Updated - 13 Aug 2024 - Added the Coach's notes section to the return JSX
  // Updated - 14 Aug 2024 - Added the Coach notes directly as ref to user session. No need for CoachOrgTrailSessionPadsQuery (removed)
  // Updated - 23 Sep 2024 - Updated Actions Display Comp - ByGroupWithTabs
  // Updated - 22 Oct 2024 - Added all the refinements done to 8848KYC component
  // Updated - 23 Feb 2025 - Copied and Renamed for prod deployment
  // Updated - 17 May 2025 - Switched the Create Action component to EtModalCreateUserActionLanding
  // Updated - 18 May 2025 - Switched the filteredUserSessionData.sessionId.sessionName to filteredUserSessionData.sessionId.sessionSequence in the Header Section.
  // Updated - 18 May 2025 - Added the back navigate button
  // Updated - 04 Nov 2025 - changes for sharedWith - to share a note with the team.
  // Updated - 05 Nov 2025 - changes for sharedWith - users query
  // Updated - 05 Nov 2025 - changes for preventing notes from deletion for notes with sharedWith list
  // Updated - 26 May 2026 - refined for session q&a
  // Updated - 23 Jun 2026 - changes for cohortId
  // Updated - 04 Jul 2026 - changes for cohortId for session challenge eval
  // Updated - 21 Jul 2026 - changes for note text sizes
  // Updated - 30 Jul 2026 - changes for charting session challenge evals
  // Updated - 01 Aug 2026 - made self evals as a separate comp
  // Updated - 20 Aug 2026 - refinements for session challenge assignment check
  // Updated - 07 Oct 2026 - refinements for session Q&A assignment check
  // Updated - 08 Oct 2026 - Session Reference Content
  // Updated - 29 Oct 2026 - Coach Shared Session Evaluations 

  // 0 - Set page Title
  useTitle('User Session - Introspection')

    // 07 Oct 2026 - EtAdmin / EtCoach retain full Session Q&A visibility
  const {
    isEtAdmin,
    isEtCoach,
  } = useAuth()

  // 1 - Extrat userId, TrailId and SessionId from the page url
  const {userId, trailId, sessionId} = useParams()
  const navigate = useNavigate() // 18 May 2025

  // 1a - Initialize showDetails state as an object where key is the organization index
  const [showDetails, setShowDetails] = useState(false)
  
  // 2 - Extract user introspection session data from the DB using RTK query
  const {data: userIntrospectionSessionData, isLoading, isError, error} = useGetUserSessionIntrospectionsQuery()

  const { data: userActionsData, refetch: refetchUserActions } = useGetUserActionsQuery(); // 17 May 2025
  // console.log('user introspection session data:', userIntrospectionSessionData)
  // console.log('coach inputs:', coachOrgTrailSessionPadsData)


  // 23 Jun 2026
  const {
    data: orgTrailSessionCohortsData,
    isLoading: isCohortsLoading,
    isError: isCohortsError,
    error: cohortsError,
  } = useGetOrgTrailSessionCohortsQuery()
  
  
  // 3a - Filter data for the matching user, trail and session -- 03 Jun 2024 --
  const userSessions = userIntrospectionSessionData?.entities ? Object.values(userIntrospectionSessionData?.entities) : [];

  // 23 Jun 2026

  // Resolve the user’s organization from the session data.
  // This avoids relying on cohort data before we know which user/trail context applies.
  const matchingUserSessionForContext = userSessions?.find((session) =>
    session?.userId?._id?.toString() === userId?.toString() &&
    session?.trailId?._id?.toString() === trailId?.toString() &&
    session?.sessionId?._id?.toString() === sessionId?.toString()
  );

  const userOrgId =
  matchingUserSessionForContext?.userId?.userOrgId?._id ||
  matchingUserSessionForContext?.userId?.userOrgId;

  const orgTrailSessionCohorts = orgTrailSessionCohortsData?.entities
    ? Object.values(orgTrailSessionCohortsData.entities)
    : [];

  const matchingCohort = orgTrailSessionCohorts.find((cohort) => {
    const cohortOrgId = cohort?.orgId?._id || cohort?.orgId;
    const cohortTrailId = cohort?.trailId?._id || cohort?.trailId;

    const hasUserInCohort = cohort?.cohortUserIds?.some((cohortUser) => {
      const cohortUserId = cohortUser?.userId?._id || cohortUser?.userId;

      return cohortUserId?.toString() === userId?.toString();
    });

    return (
      cohort?.active === true &&
      cohortOrgId?.toString() === userOrgId?.toString() &&
      cohortTrailId?.toString() === trailId?.toString() &&
      hasUserInCohort
    );
  });

  const resolvedCohortId = matchingCohort?._id;

  // 20 Aug 2026 - Get Session Challenge assignments for this user context
  const {
    data: userSessionChallengeAssignmentsData,
    isLoading: isChallengeAssignmentsLoading,
    isError: isChallengeAssignmentsError,
    error: challengeAssignmentsError,
  } = useGetUserSessionChallengeAssignmentsQuery(
    {
      orgId: userOrgId,
      trailId,
      sessionId,
      cohortId: resolvedCohortId,
    },
    {
      skip:
        !userOrgId ||
        !trailId ||
        !sessionId ||
        !resolvedCohortId,
    }
  )

  const filteredUserSessionData = userSessions?.find((session) => {
    const sessionCohortId = session?.cohortId?._id || session?.cohortId;

    return (
      session?.userId?._id?.toString() === userId?.toString() &&
      session?.trailId?._id?.toString() === trailId?.toString() &&
      session?.sessionId?._id?.toString() === sessionId?.toString() &&
      sessionCohortId?.toString() === resolvedCohortId?.toString()
    );
  });

  // console.log('filtered user session cohort:', {
  //   resolvedCohortId,
  //   sessionCohortId:
  //     filteredUserSessionData?.cohortId?._id ||
  //     filteredUserSessionData?.cohortId,
  //   filteredUserSessionData,
  // });

    // 07 Oct 2026 - Get Session Q&A assignments for this cohort context
  // EtAdmin and EtCoach retain the prior behavior and see all Session Q&A Sets.
  // Do not filter by active here. An inactive assignment still means that
  // explicit cohort configuration exists for this Session Q&A Set.
  const {
    data: sessionQNAAssignmentsData,
    isLoading: isSessionQNAAssignmentsLoading,
    isError: isSessionQNAAssignmentsError,
    error: sessionQNAAssignmentsError,
  } = useGetOrgTrailSessionQNAAssignmentsQuery(
    {
      orgId: userOrgId,
      trailId,
      sessionId,
      cohortId: resolvedCohortId,
    },
    {
      skip:
        isEtAdmin ||
        isEtCoach ||
        !userOrgId ||
        !trailId ||
        !sessionId ||
        !resolvedCohortId,
    }
  )

  // 07 Oct 2026 - Normalize Session Q&A assignments from the RTK entity adapter
  const sessionQNAAssignments = useMemo(() => {
    if (!sessionQNAAssignmentsData?.ids) {
      return []
    }

    return sessionQNAAssignmentsData.ids
      .map((id) => sessionQNAAssignmentsData?.entities[id])
      .filter(Boolean)
  }, [sessionQNAAssignmentsData])

  /*
  |--------------------------------------------------------------------------
  | 08 Oct 2026 - Get Assigned Session Reference Content
  |--------------------------------------------------------------------------
  |
  | The backend determines visibility using the authenticated
  | user's cohort membership.
  |
  | The frontend supplies only Org + Trail + Session.
  |
  | For the initial integration, this is restricted to the
  | participant view. Coach / Admin viewing another user's
  | board requires a separately authorized retrieval path.
  |
  |--------------------------------------------------------------------------
  */

  const {
    data: sessionReferenceContentData,
    isLoading: isSessionReferenceContentLoading,
    isError: isSessionReferenceContentError,
    error: sessionReferenceContentError,
  } = useGetMyAssignedSessionReferenceContentQuery(
    {
      orgId: userOrgId,
      trailId,
      sessionId,
    },
    {
      skip:
        isEtAdmin ||
        isEtCoach ||
        !userOrgId ||
        !trailId ||
        !sessionId ||
        !resolvedCohortId,
    }
  )


/*
|--------------------------------------------------------------------------
| 08 Oct 2026 - Normalize Session Reference Content
|--------------------------------------------------------------------------
|
| The RTK Query apiSlice returns normalized entity data.
|
|--------------------------------------------------------------------------
*/

const assignedSessionReferenceContent = useMemo(() => {

  if (!sessionReferenceContentData?.ids) {
    return []
  }

  return sessionReferenceContentData.ids
    .map(
      id =>
        sessionReferenceContentData.entities[id]
    )
    .filter(Boolean)
    .sort(
      (a, b) =>
        (a.displayOrder ?? 0) -
        (b.displayOrder ?? 0)
    )

}, [sessionReferenceContentData])

// 11 Oct 2026 - Temporary Session Reference Content diagnostics
console.log("Session Reference Content Debug:", {

    userId,

    userOrgId,

    trailId,

    sessionId,

    resolvedCohortId,

    isEtAdmin,

    isEtCoach,

    querySkipped:
        isEtAdmin ||
        isEtCoach ||
        !userOrgId ||
        !trailId ||
        !sessionId ||
        !resolvedCohortId,

    sessionReferenceContentData,

    assignedSessionReferenceContent,

    contentCount:
        assignedSessionReferenceContent.length,

})


  // 23 Jun 2026 - commented prior code
  // const filteredUserSessionData = userSessions?.find(session => 
  //   session.userId._id === userId && session.trailId._id === trailId && session.sessionId._id === sessionId
  // );
  // console.log('filtered user sessions data', filteredUserSessionData)

  // 3b - Extract userOrgId from filtered user session data - 22 Oct 2024
  // const userOrgId = filteredUserSessionData?.userId.userOrgId
  // console.log('User Org Id:', userOrgId)

  // 3c - Extract Coach's Shared All Users Notes from the data - 14 Aug 2024
  const coachAllUsersNotes = filteredUserSessionData?.coachSessionAllUsersNotes || []
  // console.log('coach notes:', coachAllUsersNotes)
  
  // 3d - Extract Coach's Shared User Specific Notes from the data - 14 Aug 2024
  const coachSharedUserNotes = filteredUserSessionData?.coachSessionSharedUserNotes || []
  // console.log('coach user specific notes:', coachSharedUserNotes)

  // 3e - Extract Sponsor's Shared All Users Notes from the data - 22 Oct 2024
  const sponsorAllUsersNotes = filteredUserSessionData?.sponsorSessionAllUsersNotes || []
  // console.log('sponsor notes:', sponsorAllUsersNotes)
  
  // 3f - Extract Sponsor's Shared User Specific Notes from the data - 22 Oct 2024
  const sponsorSharedUserNotes = filteredUserSessionData?.sponsorSessionSharedUserNotes || []
  // console.log('sponsor user specific notes:', sponsorSharedUserNotes)

  // 3g - Extract Challenges planned for the session - 22 Oct 2024
  // 20 Aug 2026 - meter challenge visibility using explicit assignments

  const allSessionChallenges =
    filteredUserSessionData
      ?.sessionId
      ?.sessionChallenges
      ?.map((item) => item?.challengeId)
      ?.filter(Boolean) || []

  const userSessionChallengeAssignments =
    userSessionChallengeAssignmentsData?.assignments || []

  const sessionChallenges = useMemo(() => {
    const now = new Date()

    return allSessionChallenges.filter((challenge) => {
      const challengeId =
        challenge?._id?.toString()

      if (!challengeId) {
        return false
      }

      /*
      |--------------------------------------------------------------------------
      | Find Explicit Assignment For Challenge
      |--------------------------------------------------------------------------
      */

      const assignment =
        userSessionChallengeAssignments.find((item) => {
          const assignmentChallengeId =
            item?.challengeId?._id ||
            item?.challengeId

          return (
            assignmentChallengeId?.toString() ===
            challengeId
          )
        })

      /*
      |--------------------------------------------------------------------------
      | No Explicit Assignment
      |--------------------------------------------------------------------------
      |
      | Legacy/default behavior:
      | challenge remains available to the cohort.
      |
      |--------------------------------------------------------------------------
      */

      if (!assignment) {
        return true
      }

      /*
      |--------------------------------------------------------------------------
      | Assignment Must Be Active
      |--------------------------------------------------------------------------
      */

      if (assignment?.active === false) {
        return false
      }

      /*
      |--------------------------------------------------------------------------
      | Assignment Must Be Open
      |--------------------------------------------------------------------------
      */

      if (
        assignment?.assignmentStatus !== "Open"
      ) {
        return false
      }

      /*
      |--------------------------------------------------------------------------
      | Availability Start
      |--------------------------------------------------------------------------
      */

      if (assignment?.availableFrom) {
        const availableFrom =
          new Date(
            assignment.availableFrom
          )

        if (
          !Number.isNaN(
            availableFrom.getTime()
          ) &&
          now < availableFrom
        ) {
          return false
        }
      }

      /*
      |--------------------------------------------------------------------------
      | Availability End
      |--------------------------------------------------------------------------
      */

      if (assignment?.availableUntil) {
        const availableUntil =
          new Date(
            assignment.availableUntil
          )

        if (
          !Number.isNaN(
            availableUntil.getTime()
          ) &&
          now > availableUntil
        ) {
          return false
        }
      }

      /*
      |--------------------------------------------------------------------------
      | All Cohort Users
      |--------------------------------------------------------------------------
      */

      if (
        assignment?.audienceType ===
        "AllCohortUsers"
      ) {
        return true
      }

      /*
      |--------------------------------------------------------------------------
      | Selected Users
      |--------------------------------------------------------------------------
      |
      | Backend has already validated the logged in user and calculated
      | selectedForUser.
      |
      |--------------------------------------------------------------------------
      */

      if (
        assignment?.audienceType ===
        "SelectedUsers"
      ) {
        return (
          assignment?.selectedForUser ===
          true
        )
      }

      /*
      |--------------------------------------------------------------------------
      | Unknown Explicit Assignment
      |--------------------------------------------------------------------------
      */

      return false
    })
  }, [
    allSessionChallenges,
    userSessionChallengeAssignments,
  ])

  // 20 Aug 2026 - Commented prior code
  // const sessionChallenges = filteredUserSessionData?.sessionId?.sessionChallenges?.map((item) =>{
  //   return item.challengeId
  // })
  // console.log('Session Challenges', sessionChallenges)

  // 4 - State for Modal -- 11 Jun 2024
  const [isModalOpen, setIsModalOpen] = useState(false)

  // 5 - Function to handle Add Action -- 11 Jun 2024
  const handleAddAction = () => {
      handleOpenModal()
  }

  // 5a. Modal management functions
  const handleOpenModal = () => setIsModalOpen(true)
  const handleCloseModal = () => setIsModalOpen(false)

  // 6 - Create an object for setting inner HTML
  const createMarkup = (htmlContent) => {
    return { __html: htmlContent }
  }

  // 26 May 2026 - Format session duration
  const formatSessionDuration = (durationInMinutes) => {
    const minutes = Number(durationInMinutes)

    if (!minutes) {
      return {
        value: "-",
        unit: "min",
      }
    }

    if (minutes > 60) {
      const hours = minutes / 60

      return {
        value: Number.isInteger(hours) ? hours : hours.toFixed(1),
        unit: "hrs",
      }
    }

    return {
      value: minutes,
      unit: "min",
    }
  }

  const sessionDurationDisplay = formatSessionDuration(
    filteredUserSessionData?.sessionId?.sessionEstimatedDuration
  )

  // 20 Aug 2026
  // 07 Oct 2026 - Added Session Q&A assignment loading / error state
  // 08 Oct 2026 - Added Session Reference Content loading state
  if (
    isLoading ||
    isCohortsLoading ||
    isChallengeAssignmentsLoading ||
    isSessionQNAAssignmentsLoading ||
    isSessionReferenceContentLoading
  ) {
    return <PulseLoader color={"#ea580c"} />
  }

  // 08 Oct 2026 - Added Session Reference Content error state
  if (
    isError ||
    isCohortsError ||
    isChallengeAssignmentsError ||
    isSessionQNAAssignmentsError ||
    isSessionReferenceContentError
  ) {
    return (
      <div>
        Error occurred:{" "}
        {error?.data?.message ||
          cohortsError?.data?.message ||
          challengeAssignmentsError?.data?.message ||
          sessionQNAAssignmentsError?.data?.message ||
          sessionReferenceContentError?.data?.message ||
          "Unable to load Session data."}
      </div>
    )
  }

  // // 20 Aug 2026
  // if (
  //   isLoading ||
  //   isCohortsLoading ||
  //   isChallengeAssignmentsLoading
  // ) {
  //   return <PulseLoader color={"#ea580c"} />
  // }

  // if (
  //   isError ||
  //   isCohortsError ||
  //   isChallengeAssignmentsError
  // ) {
  //   return (
  //     <div>
  //       Error occurred:{" "}
  //       {error?.data?.message ||
  //         cohortsError?.data?.message ||
  //         challengeAssignmentsError?.data?.message}
  //     </div>
  //   )
  // }

  // 23 Jun 2026
  // if (isLoading || isCohortsLoading) {
  //   return <PulseLoader color={"#ea580c"} />
  // }

  // if (isError || isCohortsError) {
  //   return (
  //     <div>
  //       Error occurred:{" "}
  //       {error?.data?.message || cohortsError?.data?.message}
  //     </div>
  //   )
  // }

  // if (isLoading) return <PulseLoader color={"#ea580c"} />
  // if (isError) return <div>Error occurred: {error?.data?.message}</div>

  return (
    <div className="bg-gray-200 p-1 mx-auto my-2 rounded-lg shadow-md shadow-black font-poppins">
      <div className='m-1 p-1 bg-white rounded'>
        {/* Header Bar */}
        <div className='m-1 p-1 bg-gray-300 rounded'>
          <div className="m-1 p-1" onClick={() => navigate("/etapp")}> {/* 18 May 2025*/}
            <ArrowLeftCircleIcon className="h-5 w-5 text-orange-600 cursor-pointer" />
          </div>
          <div className='flex justify-between items-center m-1 p-1'>
            {/* Left side */}
            <div className='m-1 p-1 flex justify-start items-center'>
              <div
                className="flex justify-center items-center cursor-pointer rounded-full h-7 w-7 hover:bg-white m-1 p-1"
                onClick={() => setShowDetails((prev) => !prev)}
              >
                {showDetails ? (
                  <MinusIcon className="h-5 w-5 text-orange-600" />
                ) : (
                  <PlusIcon className="h-5 w-5 text-orange-600" />
                )}
              </div>
              <div className='flex justify-center items-center rounded-full bg-gray-200 hover:bg-white h-7 w-7 m-1 p-1'>
                <img src={filteredUserSessionData?.sessionId?.pic} alt="" className='h-5 w-5' />
              </div>
              <div className='m-1 p-1'>
                {/* <p className='text-sm font-bold mx-1'>Session {filteredUserSessionData.userSessionNumber} - {filteredUserSessionData.sessionId.sessionName}</p> */}
                <p className='text-sm font-bold mx-1'>Session {filteredUserSessionData?.sessionId?.sessionSequence} - {filteredUserSessionData?.sessionId?.sessionName}</p>
                <p className='text-[10px] m-1'>{filteredUserSessionData?.sessionId?.sessionDesc}</p>
              </div>
            </div>

            {/* Mid Section - Status */}
            <div className='flex justify-start items-center m-1 p-1'>
              
                {/* <div className='mx-1'><ClockIcon className="h-5 w-5 text-orange-600" /></div> */}
                <div>
                  <p className='text-xs font-bold mx-1'>Duration:</p>
                </div>

                {/* 26 May 2026 */}
                <div className='rounded-full flex justify-center items-center h-5 w-5 border border-orange-300 bg-gray-50 m-1'>
                  <p className='text-orange-600 text-[10px]'>
                    {sessionDurationDisplay.value}
                  </p>
                </div>
                <div>
                  <p className='text-[8px] text-orange-600 italic'>
                    {sessionDurationDisplay.unit}
                  </p>
                </div>
              
                {/* <div className='rounded-full flex justify-center items-center h-5 w-5 border border-orange-300 bg-gray-50 m-1'><p className='text-orange-600 text-[10px]'>{filteredUserSessionData?.sessionId?.sessionEstimatedDuration}</p></div>
                <div><p className='text-[8px] text-orange-600 italic'>min</p></div> */}
            </div>

            {/* Right Side */}
            <div className='flex justify-start items-center m-1 p-1'>
              <div>
                    <RocketLaunchIcon className="h-5 w-5 text-orange-600" />
              </div>
              <div className='m-1 p-1'>
                <p className='text-xs font-bold mx-1'>Started</p>
                <p className='text-[8px] mx-1 italic text-gray-600'>{getTimeSince(filteredUserSessionData?.startedAt)}</p> {/* Display duration from the start of the session */}
              </div>
            </div>

          </div>
          {/* Session Detail when the plus icon is clicked */}
          {showDetails && (
          <div className='bg-white rounded mx-2  my-1'>
          {filteredUserSessionData?.sessionId?.sessionIntro && (
         <div className='p-2 grid grid-cols-3 gap-2 justify-center'> 
             <div className='mx-2 border-r  border-orange-200 col-span-1 h-full'>
                 <div className='flex flex-col justify-start'>
                     <div className='flex justify-start items-center  m-1 p-1'>
                         <div className=''><Square3Stack3DIcon className='h-5 w-5 text-orange-600' /></div>
                         <p className='m-1 p-1 text-xs text-gray-400 font-bold border-b-2 border-orange-600'>Session <span className='text-orange-600'>Detail</span></p>
                     </div>
                     <p dangerouslySetInnerHTML={createMarkup(filteredUserSessionData?.sessionId?.sessionIntro)} className='fix-nested-lists text-[10px] text-gray-600'></p>
                 </div>
             </div>

             <div className='mx-2 col-span-1 border-r border-orange-200 h-full'>
                 <div className='flex flex-col justify-start'>
                     <div className='flex justify-start items-center  m-1 p-1'>
                         <div className=''><ListBulletIcon className='h-5 w-5 text-orange-600' /></div>
                         <p className='m-1 p-1 text-xs text-gray-400 font-bold border-b-2 border-orange-600'>Session <span className='text-orange-600'>Prework</span></p>
                     </div>

                     {filteredUserSessionData?.sessionId?.sessionPreWork && filteredUserSessionData?.sessionId?.sessionPreWork?.map((item,preWorkIndex) => (
                     <div key={preWorkIndex} className='rounded m-1 p-1 bg-gray-100'>
                         <p className='text-[10px] mx-1'><span className='font-bold'></span>{item.prework}</p>
                     </div>
                     ))}
                 </div>
             </div>

             <div className='mx-2 col-span-1'>
                 <div className='flex flex-col justify-start'>
                     <div className='flex justify-start items-center m-1 p-1'>
                     <div className=''><SwatchIcon className='h-5 w-5 text-orange-600' /></div>
                         <p className='m-1 p-1 text-xs text-gray-400 font-bold border-b-2 border-orange-600'>Session <span className='text-orange-600'>Outcomes</span></p>
                     </div>

                     {filteredUserSessionData?.sessionId?.sessionOutcomes && filteredUserSessionData?.sessionId?.sessionOutcomes?.map((item,outIndex) => (
                     <div key={outIndex} className='rounded m-1 p-1 bg-gray-100'>
                         <p className='text-[10px] mx-1'>{item?.outcome}</p>
                     </div>
                     ))}
                 </div>
             </div>
         </div>)}
     </div>
          )}
        </div> {/* End of Header Bar */}

        {/* 23 Jun 2026 - Active Cohort Information */}
        <div className="m-1 p-1 bg-white border border-orange-200 rounded">
          <div className="flex justify-start items-center">
            <div className="rounded-full w-7 h-7 flex justify-center items-center bg-gray-100 m-1 p-1">
              <UserGroupIcon className="h-5 w-5 text-orange-600" />
            </div>

            <div className="m-1 p-1">
              <p className="text-xs font-bold text-gray-700">
                Engagement Cohort
              </p>

              <div className="flex justify-start items-center flex-wrap">
                <p className="text-[11px] text-orange-600 font-bold mr-2">
                  {matchingCohort?.cohortName || "Cohort"}
                </p>

                {matchingCohort?.cohortCode && (
                  <p className="text-[9px] text-gray-500 italic">
                    ({matchingCohort.cohortCode})
                  </p>
                )}
              </div>

              {matchingCohort?.cohortDesc && (
                <p className="text-[10px] text-gray-500 mt-1">
                  {matchingCohort.cohortDesc}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Coach's inputs - TBD */}

        {/* Input Form  */}
        {/* <EtUpdateUserSessionForm userId={userId} trailId={trailId} sessionId={sessionId} userOrgId={userOrgId} filteredUserSessionData={filteredUserSessionData} handleAddAction={handleAddAction} coachAllUsersNotes={coachAllUsersNotes} coachSharedUserNotes={coachSharedUserNotes} sponsorAllUsersNotes={sponsorAllUsersNotes} sponsorSharedUserNotes={sponsorSharedUserNotes} sessionChallenges={sessionChallenges}/> */}

        <EtUpdateUserSessionForm 
            userId={userId} 
            trailId={trailId} 
            sessionId={sessionId} 
            userOrgId={userOrgId} 
            filteredUserSessionData={filteredUserSessionData} 
            handleAddAction={handleAddAction} 
            coachAllUsersNotes={coachAllUsersNotes} 
            coachSharedUserNotes={coachSharedUserNotes} 
            sponsorAllUsersNotes={sponsorAllUsersNotes} 
            sponsorSharedUserNotes={sponsorSharedUserNotes} 
            sessionChallenges={sessionChallenges} 
            userActionsData={userActionsData} // 17 May 2025
            // 25 Jun 2026
            cohortId={resolvedCohortId}
            cohort={matchingCohort}
            // 07 Oct 2026 - Session Q&A cohort assignments
            sessionQNAAssignments={sessionQNAAssignments}
            isEtAdmin={isEtAdmin}
            isEtCoach={isEtCoach}
            // 08 Oct 2026 - Assigned Session Reference Content
            sessionReferenceContent={assignedSessionReferenceContent}
        />

      </div>
      {/* <EtModalCreateUserAction isOpen={isModalOpen} onClose={handleCloseModal} userId={userId} trailId={trailId} sessionId={sessionId} sessionTypeName={filteredUserSessionData?.sessionId.sessionTypeId?.sessionTypeName}/> */}
      
      {/* 17 May 2025 */}
      <EtModalCreateUserActionLanding
        showModal={isModalOpen}
        onClose={handleCloseModal}
        refetchUserActions={refetchUserActions} 
        defaultTrailId={trailId}                
        defaultSessionId={sessionId} 
      />

    </div>
  )
}

const EtUpdateUserSessionForm = ({
  userId, 
  trailId, 
  sessionId, 
  userOrgId, 
  filteredUserSessionData, 
  handleAddAction, 
  coachAllUsersNotes, 
  coachSharedUserNotes, 
  sponsorAllUsersNotes,
  sponsorSharedUserNotes,
  sessionChallenges,
  userActionsData, 
  //25 Jun 2026
  cohortId,
  cohort,
  // 07 Oct 2026 - Session Q&A cohort assignments
  sessionQNAAssignments,
  isEtAdmin,
  isEtCoach,
  // 08 Oct 2026 - Session Reference Content
  sessionReferenceContent,
}) => {

  // First Created - 04 Jun 2024
  // Author - Atul Pandey
  // Updated - 11 Jun 2024 - handleAddAction Modal
  // Updated - 12 Aug 2024 - Coach's Inputs
  // Updated - 13 Aug 2024 - Coach's Inputs show details
  // Updated - 14 Aug 2024 - Refined the data access based on incoming data structure from user session query
  // Updated - 22 Oct 2024 - Refined to be in line with 8848KYC (reference)
  // Updated - 17 May 2025 - Refined userActions Data - get it as a prop
  // Updated - 16 Aug 2025 - className change for notes display - fix-nested-lists
  // Updated - 04 Nov 2025 - changes for sharedWith - to share a note with the team.
  // Updated - 05 Nov 2025 - changes for sharedWith - users query
  // Updated - 05 Nov 2025 - changes for sharedWith - added confirmation modal
  // Updated - 05 Nov 2025 - changes for preventing notes from deletion for notes with sharedWith list
  // Updated - 12 Nov 2025 - changes for handleSaveNote to send only the changed note and not all notes to backend.
  // Updated - 13 Nov 2025 - sorted the session notes by updateDate
  // Updated - 26 May 2026 - refined for session q&a
  // Updated - 23 Jun 2026 - changes for cohortId
  // Updated - 04 Jul 2026 - changes for cohortId for session challenge eval
  // Updated - 21 Jul 2026 - changes for note text sizes
  // Updated - 30 Jul 2026 - changes for charting session challenge evals
  // Updated - 01 Aug 2026 - made self evals as a separate comp
  // Updated - 20 Aug 2026 - refinements for session challenge assignment check
  // Updated - 07 Oct 2026 - refinements for session Q&A assignment check
  // Updated - 08 Oct 2026 - Session Reference Content
  // Updated - 29 Oct 2026 - Coach Shared Session Evaluations


  // 0 - Define and declare Navigation and state variables
  const navigate = useNavigate()
  const [introspectionSessionNotes, setIntrospectionSessionNotes] = useState(filteredUserSessionData?.introspectionSessionNotes)
  const [isEditing, setIsEditing] = useState(false) // 14 Jun 2024 - for show / edit editor
  const [showCoachSectionDetails, setShowCoachSectionDetails] = useState(false) // 13 Aug 2024
  const [showCoachAllUsersNotes, setShowCoachAllUsersNotes] = useState(false) // 13 Aug 2024
  const [showCoachSelfUserNotes, setShowCoachSelfUserNotes] = useState(false) // 13 Aug 2024
  const [showSponsorSectionDetails, setShowSponsorSectionDetails] = useState(false) // 22 Oct 2024
  const [showSponsorAllUsersNotes, setShowSponsorAllUsersNotes] = useState(false) // 22 Oct 2024
  const [showSponsorSelfUserNotes, setShowSponsorSelfUserNotes] = useState(false) // 22 Oct 2024
  const [showSessionNotes, setShowSessionNotes] = useState(false) // 22 Oct 2024
  const [showSessionActions, setShowSessionActions] = useState(false) // 22 Oct 2024

  const [showCoachAllUsersNotesDetail,setShowCoachAllUsersNotesDetail ] = useState({}) // 22 Oct 2024 - for toggling coach all users note detail
  const [showCoachSelfUserNotesDetail,setShowCoachSelfUserNotesDetail] = useState({}) // 22 Oct 2024 - for toggling coach specific user note detail
  
  const [showSponsorAllUsersNotesDetail,setShowSponsorAllUsersNotesDetail ] = useState({}) // 22 Oct 2024 - for toggling coach all users note detail
  const [showSponsorSelfUserNotesDetail,setShowSponsorSelfUserNotesDetail] = useState({}) // 22 Oct 2024 - for toggling coach specific user note detail
  const [showSessionNotesDetail, setShowSessionNotesDetail] = useState({}) // 22 Oct 2024 - To toggle show / hide note detail

  // 29 Oct 2026 - Collapsible Coach Evaluations Section
  const [
    showCoachEvaluations,
    setShowCoachEvaluations,
  ] = useState(false)

  // 21 Jul 2026 - Default text size for expanded session notes
  const [sessionNoteTextSize, setSessionNoteTextSize] =
    useState("medium")

  // 21 Jul 2026 - Individual note text size overrides
  const [
    sessionNoteTextSizeByKey,
    setSessionNoteTextSizeByKey,
  ] = useState({})

  const sessionNoteTextSizeOptions = [
    {
      value: "small",
      label: "S",
      title: "Small text",
    },
    {
      value: "medium",
      label: "M",
      title: "Medium text",
    },
    {
      value: "large",
      label: "L",
      title: "Large text",
    },
    {
      value: "extraLarge",
      label: "XL",
      title: "Extra large text",
    },
  ]

  const sessionNoteTextSizeClasses = {
    small: "text-[11px] leading-5",
    medium: "text-sm leading-6",
    large: "text-base leading-7",
    extraLarge: "text-lg leading-8",
  }

  const getSessionNoteTextSize = (noteKey) => {
    return (
      sessionNoteTextSizeByKey[noteKey] ||
      sessionNoteTextSize
    )
  }

  const getSessionNoteTextSizeClass = (noteKey) => {
    const selectedSize = getSessionNoteTextSize(noteKey)

    return (
      sessionNoteTextSizeClasses[selectedSize] ||
      sessionNoteTextSizeClasses.medium
    )
  }

  const handleSessionNoteTextSizeChange = (
    noteKey,
    selectedSize
  ) => {
    setSessionNoteTextSizeByKey((previous) => ({
      ...previous,
      [noteKey]: selectedSize,
    }))
  }

  // 05 Nov 2025
  const [introspectionSessionTeamNotes, setIntrospectionSessionTeamNotes] = useState(filteredUserSessionData?.introspectionSessionTeamNotes) 
  const [showSessionTeamNotes, setShowSessionTeamNotes] = useState(false) 
  const [showSessionTeamNotesDetail, setShowSessionTeamNotesDetail] = useState({})
  
  // state for modal
  const [showShareConfirmModal, setShowShareConfirmModal] = useState(false);
  const [noteToShare, setNoteToShare] = useState(null);

  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);

  // console.log('filtered user sessions data', filteredUserSessionData)
  // console.log('introspectionSessionNotes', introspectionSessionNotes)
  // console.log('introspectionSessionTeamNotes', introspectionSessionTeamNotes)

  // 1a - Define the RTK mutation to update backend data
  const [updateUserSessionIntrospection] = useUpdateUserSessionIntrospectionMutation()

  // 1b - Extract User Actions Data using RTK query to filter and count the user actions for the session - 22 Oct 2024
  // const {data: userActionsData} = useGetUserActionsQuery() // Extract data in entities adapter
  
  const userActions = userActionsData?.ids?.map((id) => {  // Extract and separate the data inside entities adapter
    const userAction = userActionsData?.entities[id]
    return userAction
  })
  const filteredUserActions = userActions?.filter((action) => 
    action?.userId?._id === userId &&
    action?.trailId?._id === trailId &&
    action?.sessionId?._id === sessionId
  )
 
  const userActionsCount = filteredUserActions?.length
  // console.log('userActions count:', userActionsCount)

  // 1c - Editor Value setting based on incoming data -- 13 Jun 2024
  const [editorValue, setEditorValue] = useState(() => {
  const latestNote = filteredUserSessionData?.introspectionSessionNotes?.slice(-1)[0];
  return latestNote ? latestNote.introspectionNoteText : '';
  });

  // 1d - Current Note Id of the note currently in editor -- 14 Jun 2024
  const [currentNoteId, setCurrentNoteId] = useState(() => {
    const latestNote = filteredUserSessionData?.introspectionSessionNotes?.slice(-1)[0];
    return latestNote ? latestNote._id : null;
  });

  // 1e - Extract Self User Data - to be passed to User component to get picture - 22 Oct 2024
  const selfUserData = filteredUserSessionData?.userId
  // console.log('filtered user data', selfUserData)

  // 2a - 05 Nov 2025 - User's data
  const {data: allUsersData} = useGetUsersQuery();

  const extractEntitiesFromAdapter = (dataAdapter) => {
    if (!dataAdapter || !dataAdapter.ids || !dataAdapter.entities) return [];
    return dataAdapter.ids.map((id) => dataAdapter.entities[id]);
  };

  const allUsers = extractEntitiesFromAdapter(allUsersData);
  const allOrgUsers = allUsers?.filter(user => user?.userOrgId?._id === userOrgId)
  // console.log('All Users', allUsers)
  // console.log('All Org Users', allOrgUsers)


  // 3a - Handle Editor Value change
  const handleEditorChange = (value) => {
    setEditorValue(value);
  };
  
  // 3b1 - Form submit
  const handleSave = async () => {
    try {
        let updatedNotes;
        if (currentNoteId) {
          // Update existing note
          updatedNotes = introspectionSessionNotes.map(note =>
            note._id === currentNoteId ? { ...note, introspectionNoteText: editorValue, introspectionNoteUpdateDate: new Date() } : note
          );
        } else {
          // Add new note
          const newNote = {
            introspectionNoteText: editorValue,
            introspectionNoteDate: new Date(),
            introspectionNoteUpdateDate: new Date(),
          };
          updatedNotes = [...introspectionSessionNotes, newNote];
        }
  
      await updateUserSessionIntrospection({
        userId,
        trailId,
        sessionId,
        introspectionSessionNotes: updatedNotes,
      }).unwrap();
  
      // navigate('/userMagicHome')
      navigate('/etapp') // 23 Feb 2025
    } catch (error) {
      console.error('Failed to update the user session introspection:', error);
      // You can add an error message or any other logic here in case of a failure
    }
  };

  // 3b2 - 05 Nov 2025 - Share Note with Team
  const handleShareNote = async (noteToShare) => {
    try {
      // Step 1 - Build sharedWith array from org users
      const sharedWith = allOrgUsers.map(user => ({
        userId: user._id
      }));

      // Step 2 - Update the matching note in introspectionSessionNotes
      const updatedNotes = introspectionSessionNotes.map(note => {
        if (note._id === noteToShare._id) {
          return {
            ...note,
            sharedWith: sharedWith
          };
        }
        return note;
      });

      // Step 3 - Send update to backend
      await updateUserSessionIntrospection({
        userId,
        trailId,
        sessionId,
        introspectionSessionNotes: updatedNotes
      }).unwrap();

      alert('Note shared with team successfully');
      window.location.reload();
    } catch (error) {
      console.error('Failed to share note with team:', error);
      alert('Something went wrong while sharing the note.');
    }
  };


  // 3c - Function to handle saving new Note -- 14 Jun 2024 --
  const handleSaveNote = async () => {
    if (editorValue.trim() === '') {
      alert('The editor is blank. Please enter some text before saving.');
      return;
    }
  
    try {
        // let updatedNotes; // 12 Nov 2025
        let notePayload; // 12 Nov 2025
        if (currentNoteId) {
          // Update existing note
          // updatedNotes = introspectionSessionNotes.map(note =>
          //   note._id === currentNoteId ? { ...note, introspectionNoteText: editorValue, introspectionNoteUpdateDate: new Date() } : note
          // );

          // ✅ 12 Nov 2025 - Update existing note — only send the edited one
          const updatedNote = {
            ...introspectionSessionNotes.find(note => note._id === currentNoteId),
            introspectionNoteText: editorValue,
            introspectionNoteUpdateDate: new Date(),
          };
          notePayload = [updatedNote];
        } else {
          // Add new note
          // const newNote = {
          //   introspectionNoteText: editorValue,
          //   introspectionNoteDate: new Date(),
          //   introspectionNoteUpdateDate: new Date(),
          // };
          // updatedNotes = [...introspectionSessionNotes, newNote];

          // ✅ 12 Nov 2025 - Add new note — only send the new one
          const newNote = {
            introspectionNoteText: editorValue,
            introspectionNoteDate: new Date(),
            introspectionNoteUpdateDate: new Date(),
          };
          notePayload = [newNote];
        }
  
      await updateUserSessionIntrospection({
        userId,
        trailId,
        sessionId,
        // introspectionSessionNotes: updatedNotes,
        introspectionSessionNotes: notePayload, // 12 Nov 2025
      }).unwrap();
  
      setEditorValue('');
      setIsEditing(false);
      alert('Notes updated successfully');
      window.location.reload();
    } catch (error) {
      console.error('Failed to update the user session introspection:', error);
      // You can add an error message or any other logic here in case of a failure
    }
  };
  
  // 3d - Handle Clear
  const handleClear = () => {
    setIntrospectionSessionNotes(filteredUserSessionData?.introspectionSessionNotes)
    setIntrospectionSessionTeamNotes(filteredUserSessionData?.introspectionSessionTeamNotes) // 05 Nov 2025
  }

  // 4 - Handle Cancel
  const handleCancel = () => {
   // navigate('/userMagicHome')
   navigate('/etapp') // 23 Feb 2025
  }

  // 5 - Create an object for setting inner HTML
  const createMarkup = (htmlContent) => {
    return { __html: htmlContent }
  }

  // 6 - Function to handle Add New Note -- 14 Jun 2024 --
  const handleAddNote = () => {
    // Clear editor and switch to edit mode
    setEditorValue('');
    setCurrentNoteId(null); // Clear the currentNoteId to treat it as a new entry
    setIsEditing(true);
   };

  // 7 - Function to handle editing a note -- 14 Jun 2024 --
  const handleEditNote = (note) => {
    setEditorValue(note.introspectionNoteText);
    setCurrentNoteId(note._id);
    setIsEditing(true);
  };

  // 7a - Function to handle editing a note -- 14 Jun 2024 --
  const handleCancelEditNote = (note) => {
    setEditorValue('');
    setCurrentNoteId(null);
    setIsEditing(false);
  };

  // 8 - Function to handle deleting a note -- 14 Jun 2024 --Updated 05 Nov 2025
  const handleDeleteNote = async (noteId) => {
    try {

      // 05 Nov 2025
      // console.log('noteId in handleDeleteNote:', noteId)

      if (noteId) {
        const note = introspectionSessionNotes.find(n => n._id === noteId);
        // console.log('note.sharedWith', note?.sharedWith)
        if (note?.sharedWith?.length > 0) {
          alert("Shared notes cannot be deleted.");
          return;
        }
      }
      
      // const updatedNotes = introspectionSessionNotes?.filter(note => note._id !== noteId);  
      
      // 05 Nov 2025 - Mark the note with delete: true
      const updatedNotes = introspectionSessionNotes.map(note => {
        if (note._id === noteId) {
          return { ...note, delete: true };
        }
        return note;
      });
      
      await updateUserSessionIntrospection({
        userId,
        trailId,
        sessionId,
        introspectionSessionNotes: updatedNotes,
      }).unwrap();
      
      // setIntrospectionSessionNotes(updatedNotes); // Update the state with the filtered notes

      // 05 Nov 2025 - Update local state after successful deletion
      setIntrospectionSessionNotes(prevNotes =>
        prevNotes.filter(note => note._id !== noteId)
      );
      
      alert('Note deleted successfully');
    } catch (error) {
      console.error('Failed to delete the note:', error);
      // Add error handling logic here if needed
    }
  };

  // 9 - Calculate the number of coaches notes - 13 Aug 2024
  const allUsersNoteCount = coachAllUsersNotes?.length
  const sharedUserNoteCount = coachSharedUserNotes?.length
  

  // 10a - Calculate the number of sponsors' notes - 22 Oct 2024
  const allUsersSponsorNoteCount = sponsorAllUsersNotes?.length
  const sharedUserSponsorNoteCount = sponsorSharedUserNotes?.length

  // 10b - Calculate the number of team notes - 05 Nov 2025
  const teamNoteCount = introspectionSessionTeamNotes?.length || 0

    // 10c - Calculate Session QNA Sets count - 26 May 2026
  // 07 Oct 2026 - Filter Session Q&A Sets using explicit cohort assignments
  const sessionQNASets = filteredUserSessionData?.sessionId?.sessionQNASets || []

  // Start with the active Q&A Sets attached to the Session.
  const activeSessionQNASets = useMemo(() => {
    return (sessionQNASets || [])
      .filter(
        (item) =>
          item?.active !== false &&
          item?.sessionQNASetId
      )
      .slice()
      .sort(
        (a, b) =>
          (a?.sessionQNASetSequence || 0) -
          (b?.sessionQNASetSequence || 0)
      )
  }, [sessionQNASets])

  // Any assignment record, active or inactive, means that an explicit
  // cohort visibility configuration exists for this Session.
  const hasExplicitSessionQNAConfiguration =
    (sessionQNAAssignments || []).length > 0

  // Only active assignment records contribute Q&A Sets to the visible list.
  const assignedSessionQNASetIds = useMemo(() => {
    return new Set(
      (sessionQNAAssignments || [])
        .filter(
          (assignment) =>
            assignment?.active !== false
        )
        .map(
          (assignment) =>
            getRefId(
              assignment?.sessionQNASetId
            )
        )
        .filter(Boolean)
    )
  }, [sessionQNAAssignments])

  const visibleSessionQNASets = useMemo(() => {
    /*
    |--------------------------------------------------------------------------
    | EtAdmin / EtCoach
    |--------------------------------------------------------------------------
    |
    | Preserve the prior behavior. These roles continue to see all active
    | Session Q&A Sets attached to the Session.
    |
    |--------------------------------------------------------------------------
    */

    if (
      isEtAdmin ||
      isEtCoach
    ) {
      return activeSessionQNASets
    }

    /*
    |--------------------------------------------------------------------------
    | No Explicit Configuration
    |--------------------------------------------------------------------------
    |
    | Backward compatibility:
    | when no Q&A assignments exist for this Org + Trail + Session + Cohort,
    | continue to show all active Session Q&A Sets.
    |
    |--------------------------------------------------------------------------
    */

    if (
      !hasExplicitSessionQNAConfiguration
    ) {
      return activeSessionQNASets
    }

    /*
    |--------------------------------------------------------------------------
    | Explicit Cohort Configuration
    |--------------------------------------------------------------------------
    |
    | Once assignment records exist, only Q&A Sets with active assignments
    | are visible to the user.
    |
    |--------------------------------------------------------------------------
    */

    return activeSessionQNASets.filter(
      (item) =>
        assignedSessionQNASetIds.has(
          getRefId(
            item?.sessionQNASetId
          )
        )
    )
  }, [
    activeSessionQNASets,
    assignedSessionQNASetIds,
    hasExplicitSessionQNAConfiguration,
    isEtAdmin,
    isEtCoach,
  ])

  const sessionQNASetCount = visibleSessionQNASets?.length || 0

  // // 10c - Calculate Session QNA Sets count - 26 May 2026
  // const sessionQNASets = filteredUserSessionData?.sessionId?.sessionQNASets || []
  // const sessionQNASetCount = sessionQNASets?.filter(item => item?.active !== false)?.length || 0

  // console.log('allUsersNoteCount :', allUsersNoteCount)
  // console.log('sharedUserNoteCount :', sharedUserNoteCount)
  // console.log('allUsersSponsorNoteCount :', allUsersSponsorNoteCount)
  // console.log('sharedUserSponsorNoteCount :', sharedUserSponsorNoteCount)

  // 11a - Handle Toggle showCoachAllUsersNotesDetail - 22 Oct 2024
  const handleToggleShowCoachAllUsersNotesDetail = (noteId) => {
    setShowCoachAllUsersNotesDetail((prev) => ({
      ...prev,
      [noteId]: !prev[noteId],
    }))
  }
  
  // 11b - Handle Toggle showCoachSelfUserNotesDetail - 22 Oct 2024
  const handleToggleShowCoachSelfUserNotesDetail = (noteId) => {
    setShowCoachSelfUserNotesDetail((prev) => ({
      ...prev,
      [noteId]: !prev[noteId],
    }))
  }

  // 11c - Handle Toggle showSponsorAllUsersNotesDetail - 22 Oct 2024
  const handleToggleShowSponsorAllUsersNotesDetail = (noteId) => {
    setShowSponsorAllUsersNotesDetail((prev) => ({
      ...prev,
      [noteId]: !prev[noteId],
    }))
  }
  
  // 11d - Handle Toggle showSponsorSelfUserNotesDetail - 22 Oct 2024
  const handleToggleShowSponsorSelfUserNotesDetail = (noteId) => {
    setShowSponsorSelfUserNotesDetail((prev) => ({
      ...prev,
      [noteId]: !prev[noteId],
    }))
  }

  // 11e - Handle Toggle show session UserNotesDetail - 22 Oct 2024
  const handleToggleShowSessionNotesDetail = (noteId) => {
    setShowSessionNotesDetail((prev) => ({
      ...prev,
      [noteId]: !prev[noteId],
    }))
  }

  // 11f - Handle Toggle show session TeamNotesDetail - 05 Nov 2025
  const handleToggleShowSessionTeamNotesDetail = (noteId) => {
    setShowSessionTeamNotesDetail((prev) => ({
      ...prev,
      [noteId]: !prev[noteId],
    }))
  }


  // 21 Jul 2026 - Text size selector used inside each expanded note
  const renderSessionNoteTextSizeSelector = (noteKey) => {
    const selectedSize = getSessionNoteTextSize(noteKey)

    return (
      <div className="mb-2 flex justify-end">
        <div className="flex items-center gap-1 rounded border border-gray-200 bg-white px-2 py-1">
          <p className="mr-1 text-[9px] font-semibold text-gray-500">
            Text:
          </p>

          {sessionNoteTextSizeOptions.map((sizeOption) => {
            const isSelected =
              selectedSize === sizeOption.value

            return (
              <button
                key={sizeOption.value}
                type="button"
                title={sizeOption.title}
                aria-label={sizeOption.title}
                aria-pressed={isSelected}
                onClick={() =>
                  handleSessionNoteTextSizeChange(
                    noteKey,
                    sizeOption.value
                  )
                }
                className={`min-w-7 rounded px-2 py-1 text-[9px] font-semibold transition ${
                  isSelected
                    ? "bg-orange-600 text-white shadow-sm"
                    : "border border-gray-200 bg-gray-50 text-gray-500 hover:border-orange-300 hover:text-orange-600"
                }`}
              >
                {sizeOption.label}
              </button>
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <div className="bg-gray-300 p-1 m-1 rounded font-poppins">

      {/* 21 Jul 2026 - Default text size for all expanded session notes */}
      <div className="m-1 flex justify-end">
        <div className="flex items-center gap-1 rounded border border-gray-200 bg-white px-2 py-1">
          <p className="mr-1 text-[9px] font-semibold text-gray-500">
            Note text:
          </p>

          {sessionNoteTextSizeOptions.map((sizeOption) => {
            const isSelected =
              sessionNoteTextSize === sizeOption.value

            return (
              <button
                key={sizeOption.value}
                type="button"
                title={sizeOption.title}
                aria-label={sizeOption.title}
                aria-pressed={isSelected}
                onClick={() => {
                  setSessionNoteTextSize(sizeOption.value)

                  // Apply the new default to every note
                  setSessionNoteTextSizeByKey({})
                }}
                className={`min-w-7 rounded px-2 py-1 text-[9px] font-semibold transition ${
                  isSelected
                    ? "bg-orange-600 text-white shadow-sm"
                    : "border border-gray-200 bg-gray-50 text-gray-500 hover:border-orange-300 hover:text-orange-600"
                }`}
              >
                {sizeOption.label}
              </button>
            )
          })}
        </div>
      </div>

      {/*
      |--------------------------------------------------------------------------
      | 08 Oct 2026 - Session Reference Content
      |--------------------------------------------------------------------------
      |
      | Reference Content is controlled through cohort assignments.
      |
      | Videos, articles and reflection prompts are shown here
      | before Session Challenges.
      |
      |--------------------------------------------------------------------------
      */}

      <EtUserSessionReferenceContentBoard
        referenceContent={sessionReferenceContent}
      />
      
      {/* 01 Aug 2026 */}
      <EtUserSessionChallengesSection
        sessionChallenges={sessionChallenges}
        userId={userId}
        trailId={trailId}
        sessionId={sessionId}
        userOrgId={userOrgId}
        selfUserData={selfUserData}
        cohortId={cohortId}
      />

      {/* 19 Aug 2024 - Session Challenges -- 22 Oct 2024*/}
      {/* <EtSessionChallengesSection 
        sessionChallenges={sessionChallenges} 
        userId={userId} 
        trailId={trailId} 
        sessionId={sessionId} 
        userOrgId={userOrgId} 
        selfUserData={selfUserData}
        // 04 Jul 2026
        cohortId={cohortId}
      /> */}


      {/* 26 May 2026 - Session QNA Board */}
      {/* 07 Oct 2026 - Cohort based Session Q&A visibility */}
      {sessionQNASetCount > 0 && (
        <EtUserSessionQNABoard
          userId={userId}
          userOrgId={userOrgId}
          trailId={trailId}
          sessionId={sessionId}
          sessionQNASets={visibleSessionQNASets} // 07 Oct 2026
          // sessionQNASets={sessionQNASets}
          responseRole="User"
          boardTitle="Session Q&A Board"
          // 25 Jun 2026
          cohortId={cohortId}
          cohort={cohort}
        />
      )}


      
      {/*----------------------------------------------------------
        29 Oct 2026 - Step 11E
        Participant Shared Coach Evaluations
        Collapsible Section
      ----------------------------------------------------------*/}

      {!isEtAdmin && !isEtCoach && (

        <div className="m-1 p-1 border border-white rounded">

          {/* Coach Evaluations Header Block */}

          <div className="m-1 p-1 grid grid-cols-5 justify-start items-center">

            <div className="col-span-2 flex justify-start items-center">

              {/* Expand / Collapse */}

              <button
                type="button"
                className="flex justify-center items-center cursor-pointer rounded-full h-7 w-7 hover:bg-white m-1 p-1"
                onClick={() =>
                  setShowCoachEvaluations(
                    previous => !previous
                  )
                }
                aria-expanded={showCoachEvaluations}
                aria-label={
                  showCoachEvaluations
                    ? "Hide Coach Evaluations"
                    : "Show Coach Evaluations"
                }
              >

                {showCoachEvaluations ? (

                  <MinusIcon className="h-5 w-5 text-orange-600" />

                ) : (

                  <PlusIcon className="h-5 w-5 text-orange-600" />

                )}

              </button>


              {/* Section Icon */}

              <div className="rounded-full w-7 h-7 flex justify-center items-center bg-gray-200 hover:bg-gray-50 m-1 p-1">

                <ChartBarIcon className="h-5 w-5 text-orange-600" />

              </div>


              {/* Section Title */}

              <div>

                <p className="font-bold mx-1 px-1">
                  My Coach Evaluations
                </p>

                <p className="text-[10px] italic text-gray-600 mx-1">
                  See your development progress and feedback
                  shared by your coach.
                </p>

              </div>

            </div>

          </div>


          {/*------------------------------------------------------
            Expanded Coach Evaluations Board
          ------------------------------------------------------*/}

          {showCoachEvaluations && (

            <div className="m-1 p-1 rounded bg-white">

              <EtUserSessionCoachEvaluationsBoard

                orgId={userOrgId}

                trailId={trailId}

                sessionId={sessionId}

                cohortId={cohortId}

              />

            </div>

          )}

        </div>

      )}


      {/* 12 Aug 2024 - Coach's notes - 22 Oct 2024 */}
      <div className='m-1 p-1 border border-white rounded'>

          {/* Coach Inputs Header Block */}
          <div className='m-1 p-1 grid grid-cols-5 justify-start items-center'>
            <div className='col-span-2 flex justify-start items-center'>
              <div
                className="flex justify-center items-center cursor-pointer rounded-full h-7 w-7 hover:bg-white m-1 p-1"
                onClick={() => setShowCoachSectionDetails((prev) => !prev)}
              >
                {showCoachSectionDetails ? (
                  <MinusIcon className="h-5 w-5 text-orange-600" />
                ) : (
                  <PlusIcon className="h-5 w-5 text-orange-600" />
                )}
              </div>
                <div className='rounded-full w-7 h-7 flex justify-center items-center bg-gray-200 hover:bg-gray-50  m-1 p-1'><EtCoachGroupIcon1SVGComp className='w-5 h-5 text-orange-600'/>
                </div>
                <div>
                  <p className='font-bold mx-1 px-1'>Coach's Notes </p>
                  <p className='text-[10px] italic text-gray-600 mx-1'>See what your coaches have to say.. </p>
                </div>
              </div>
            
            <div className='col-span-1'>
              <div className='rounded-full flex justify-center items-center h-5 w-5 border border-orange-300 bg-gray-50 m-1'>
                <p className='text-orange-600 text-[10px]'>{allUsersNoteCount + sharedUserNoteCount}</p>
              </div>
            </div>
          </div>

          {showCoachSectionDetails && (
            <div>
                {/* All Users Notes */}
              <div className='m-1 p-1 bg-gray-100 rounded'>
                  
                  <div className='m-1 p-1 flex justify-start items-center'>
                  <div
                      className="flex justify-center items-center cursor-pointer rounded-full h-7 w-7 hover:bg-white m-1 p-1"
                      onClick={() => setShowCoachAllUsersNotes((prev) => !prev)}
                    >
                      {showCoachAllUsersNotes ? (
                        <MinusIcon className="h-5 w-5 text-orange-600" />
                      ) : (
                        <PlusIcon className="h-5 w-5 text-orange-600" />
                      )}
                    </div>
                    <div className='rounded-full w-7 h-7 flex justify-center items-center bg-gray-200 hover:bg-gray-50'><EtCoachGroupIcon2SVGComp className='w-7 h-7 text-orange-600'/></div>
                    <div><p className='text-sm text-gray-600 font-bold mx-1 px-1'>For Everyone:</p></div>
                    <div className='rounded-full flex justify-center items-center h-5 w-5 border border-orange-300 bg-gray-50 m-1'><p className='text-orange-600 text-[10px]'>{allUsersNoteCount}</p></div>
                  </div>

                  {allUsersNoteCount !== 0 && showCoachAllUsersNotes && (
                      <div className='m-1 p-1 bg-gray-300 rounded'>
                        {coachAllUsersNotes && coachAllUsersNotes?.slice()
                            .sort((a, b) => new Date(b.coachAllUsersNotesId.coachAllUsersNoteUpdateDate) - new Date(a.coachAllUsersNotesId.coachAllUsersNoteUpdateDate)).map((note, noteIndex, arr) =>(
                          
                          <div key={note._id+ noteIndex} className='m-1 p-1 bg-white rounded'>

                              <div className='grid grid-cols-6 justify-between items-center m-1 p-1 bg-gray-100 rounded'>
                                
                                <div className='col-span-1 flex justify-start items-center'>
                                  <div
                                      onClick={() => handleToggleShowCoachAllUsersNotesDetail(note._id)}
                                      className="mx-1 rounded-full h-6 w-6 hover:bg-white flex justify-center items-center cursor-pointer"
                                    >
                                      {showCoachAllUsersNotesDetail[note._id] ? (
                                        <MinusIcon className="h-4 w-4 text-orange-600" />
                                      ) : (
                                        <PlusIcon className="h-4 w-4 text-orange-600" />
                                      )}
                                    </div>
                                    <div>
                                      {/* <p className='text-2xl font-bold mx-1 px-1 text-gray-600'>{arr.length - noteIndex}.</p> */}
                                      <p className='text-2xl font-bold mx-1 px-1 text-gray-600'>{noteIndex +1}.</p>
                                    </div> 
                                    <div>
                                      <p className='text-[9px] mx-1 rounded-full px-2 py-[1px] bg-lime-200 w-18'>{getTimeSince(note?.coachAllUsersNotesId.coachAllUsersNoteUpdateDate)}</p>
                                      <p className='text-[7px] mx-1 italic px-2 py-1 text-gray-400'>last updated</p>
                                  </div>  
                                </div>
                                <div className='col-span-3 flex justify-start items-center'>
                                  <div dangerouslySetInnerHTML={createMarkup(note?.coachAllUsersNotesId.coachAllUsersNoteText)} className='text-[11px] text-gray-700 m-1 p-1 truncate max-w-full line-clamp-1'></div>
                                </div>
                                <div className='col-span-2 flex justify-start items-center mx-2'>
                                    <ClockIcon className='h-5 w-5 text-lime-600'/>
                                    <p className='text-[9px] mx-1'>First Created:</p>
                                    <p className='text-gray-400 text-[9px] mx-2'>{formatDateTime(note?.coachAllUsersNotesId.coachAllUsersNoteDate)}</p>
                                </div>
                              </div>
                              {/* 21 Jul 2026 */}
                              {showCoachAllUsersNotesDetail[note._id] && (() => {
                                const noteTextSizeKey =
                                  `coach-all-users-${note._id}`

                                return (
                                  <div className="m-1 p-1 rounded bg-gray-50">
                                    {renderSessionNoteTextSizeSelector(
                                      noteTextSizeKey
                                    )}

                                    <div
                                      dangerouslySetInnerHTML={createMarkup(
                                        note?.coachAllUsersNotesId
                                          ?.coachAllUsersNoteText
                                      )}
                                      className={`fix-nested-lists ${getSessionNoteTextSizeClass(
                                        noteTextSizeKey
                                      )} text-gray-700 m-1 p-1`}
                                    />
                                  </div>
                                )
                              })()}
                              {/* {showCoachAllUsersNotesDetail[note._id] && (
                                <div className="m-1 p-1 rounded bg-gray-50">
                                <div
                                  dangerouslySetInnerHTML={createMarkup(note?.coachAllUsersNotesId.coachAllUsersNoteText)}
                                  className="fix-nested-lists text-[11px] text-gray-700 m-1 p-1"
                                ></div>
                                </div>

                              )} */}

                          </div>
                        ))}
                      </div>
                  )}
                  {/* When no coach notes are available */}
                  {allUsersNoteCount === 0 && showCoachAllUsersNotes && (
                    <div className="m-1 p-1 bg-white rounded">
                    <p className='text-orange-600 text-xs m-1 p-1'>No coach notes are available yet.</p>
                  </div>
                  )}
              </div>
              {/* Self User Notes */}
              <div className='m-1 p-1 bg-gray-100 rounded'>
                  
                  <div className='m-1 p-1 flex justify-start items-center'>
                  <div
                      className="flex justify-center items-center cursor-pointer rounded-full h-7 w-7 hover:bg-white m-1 p-1"
                      onClick={() => setShowCoachSelfUserNotes((prev) => !prev)}
                    >
                      {showCoachSelfUserNotes ? (
                        <MinusIcon className="h-5 w-5 text-orange-600" />
                      ) : (
                        <PlusIcon className="h-5 w-5 text-orange-600" />
                      )}
                    </div>
                    <div className='rounded-full w-7 h-7 flex justify-center items-center bg-gray-200 hover:bg-gray-50'><EtCoachOneOnOneIcon2SVGComp className='w-7 h-7 text-orange-600'/></div>
                    <div><p className='text-sm text-gray-600 font-bold mx-1 px-1'>For Me:</p></div>
                    <div className='rounded-full flex justify-center items-center h-5 w-5 border border-orange-300 bg-gray-50 m-1'><p className='text-orange-600 text-[10px]'>{sharedUserNoteCount}</p></div>
                  </div>

                  {sharedUserNoteCount !== 0 && showCoachSelfUserNotes && (
                      <div className='m-1 p-1 bg-gray-300 rounded'>
                        {coachSharedUserNotes && coachSharedUserNotes?.slice()
                            .sort((a, b) => new Date(b.coachSharedUserNotesId.coachSharedUserNoteUpdateDate) - new Date(a.coachSharedUserNotesId.coachSharedUserNoteUpdateDate)).map((note, noteIndex, arr) =>(
                          
                          <div key={note._id+ noteIndex} className='m-1 p-1 bg-white rounded'>
                              <div className='grid grid-cols-6 justify-between items-center m-1 p-1 bg-gray-100 rounded'>
                                
                                <div className='col-span-1 flex justify-start items-center'>
                                  <div
                                    onClick={() => handleToggleShowCoachSelfUserNotesDetail(note._id)}
                                    className="mx-1 rounded-full h-6 w-6 hover:bg-white flex justify-center items-center cursor-pointer"
                                  >
                                    {showCoachSelfUserNotesDetail[note._id] ? (
                                      <MinusIcon className="h-4 w-4 text-orange-600" />
                                    ) : (
                                      <PlusIcon className="h-4 w-4 text-orange-600" />
                                    )}
                                    </div>
                                    <div>
                                      {/* <p className='text-2xl font-bold mx-1 px-1 text-gray-600'>{arr.length - noteIndex}.</p> */}
                                      <p className='text-2xl font-bold mx-1 px-1 text-gray-600'>{noteIndex +1}.</p>
                                      </div> 
                                    <div>
                                      <p className='text-[9px] mx-1 rounded-full px-2 py-[1px] bg-lime-200 w-18'>{getTimeSince(note?.coachSharedUserNotesId.coachSharedUserNoteUpdateDate)}</p>
                                      <p className='text-[7px] mx-1 italic px-2 py-1 text-gray-400'>last updated</p>
                                  </div>  
                                </div>
                                <div className='col-span-3 flex justify-start items-center'>
                                  <div dangerouslySetInnerHTML={createMarkup(note?.coachSharedUserNotesId.coachSharedUserNoteText)} className='text-[11px] text-gray-700 m-1 p-1 truncate max-w-full line-clamp-1'></div>
                                </div>
                                <div className='col-span-2 flex justify-start items-center mx-2'>
                                    <ClockIcon className='h-5 w-5 text-lime-600'/>
                                    <p className='text-[9px] mx-1'>First Created:</p>
                                    <p className='text-gray-400 text-[9px] mx-2'>{formatDateTime(note?.coachSharedUserNotesId.coachSharedUserNoteDate)}</p>
                                </div>
                              </div>
                              {/* 21 Jul 2026 */}
                              {showCoachSelfUserNotesDetail[note._id] && (() => {
                                const noteTextSizeKey =
                                  `coach-shared-user-${note._id}`

                                return (
                                  <div className="m-1 p-1 rounded bg-gray-50">
                                    {renderSessionNoteTextSizeSelector(
                                      noteTextSizeKey
                                    )}

                                    <div
                                      dangerouslySetInnerHTML={createMarkup(
                                        note?.coachSharedUserNotesId
                                          ?.coachSharedUserNoteText
                                      )}
                                      className={`fix-nested-lists ${getSessionNoteTextSizeClass(
                                        noteTextSizeKey
                                      )} text-gray-700 m-1 p-1`}
                                    />
                                  </div>
                                )
                              })()}


                              {/* {showCoachSelfUserNotesDetail[note._id] && (
                                <div className="m-1 p-1 rounded bg-gray-50">
                                <div
                                  dangerouslySetInnerHTML={createMarkup(note?.coachSharedUserNotesId.coachSharedUserNoteText)}
                                  className="fix-nested-lists text-[11px] text-gray-700 m-1 p-1"
                                ></div>
                                </div>

                              )} */}
                          </div>
                        ))}
                      </div>
                  )}
                  {/* When no coach notes are available */}
                  {sharedUserNoteCount === 0 && showCoachSelfUserNotes && (
                    <div className="m-1 p-1 bg-white rounded">
                    <p className='text-orange-600 text-xs m-1 p-1'>No coach notes are available yet.</p>
                  </div>
                  )}
              </div>
            </div>
          )}  

      </div>

      {/* 12 Sep 2024 - Sponsor's notes - 22 Oct 2024*/}
      <div className='m-1 p-1 border border-white rounded'>

        {/* Sponsor Inputs Header Block 14 Sep 2024 - changed flex to grid*/}
        <div className='m-1 p-1 grid grid-cols-5 justify-start items-center'>
          <div className='col-span-2 flex justify-start items-center'>
            <div
              className="flex justify-center items-center cursor-pointer rounded-full h-7 w-7 hover:bg-white m-1 p-1"
              onClick={() => setShowSponsorSectionDetails((prev) => !prev)}
            >
              {showSponsorSectionDetails ? (
                <MinusIcon className="h-5 w-5 text-orange-600" />
              ) : (
                <PlusIcon className="h-5 w-5 text-orange-600" />
              )}
            </div>
            <div className='rounded-full w-7 h-7 flex justify-center items-center bg-gray-200 hover:bg-gray-50  m-1 p-1'><EtSponsorGroupIcon1SVGComp className='w-5 h-5 text-orange-600'/>
            </div>
            <div>
              <p className='font-bold mx-1 px-1'>Sponsor's Notes </p>
              <p className='text-[10px] italic text-gray-600 mx-1'>See what your sponsors have to say.. </p>
            </div>
          </div>
          
          <div className='col-span-1'>
            <div className='rounded-full flex justify-center items-center h-5 w-5 border border-orange-300 bg-gray-50 m-1'>
              <p className='text-orange-600 text-[10px]'>{allUsersSponsorNoteCount + sharedUserSponsorNoteCount}</p>
            </div>
          </div>
            </div>

            {showSponsorSectionDetails && (
          <div>
              {/* All Users Sponsor Notes */}
            <div className='m-1 p-1 bg-gray-100 rounded'>
                
                <div className='m-1 p-1 flex justify-start items-center'>
                <div
                    className="flex justify-center items-center cursor-pointer rounded-full h-7 w-7 hover:bg-white m-1 p-1"
                    onClick={() => setShowSponsorAllUsersNotes((prev) => !prev)}
                  >
                    {showSponsorAllUsersNotes ? (
                      <MinusIcon className="h-5 w-5 text-orange-600" />
                    ) : (
                      <PlusIcon className="h-5 w-5 text-orange-600" />
                    )}
                  </div>
                  <div className='rounded-full w-7 h-7 flex justify-center items-center bg-gray-200 hover:bg-gray-50'><EtCoachGroupIcon2SVGComp className='w-7 h-7 text-orange-600'/></div>
                  <div><p className='text-sm text-gray-600 font-bold mx-1 px-1'>For Everyone:</p></div>
                  <div className='rounded-full flex justify-center items-center h-5 w-5 border border-orange-300 bg-gray-50 m-1'><p className='text-orange-600 text-[10px]'>{allUsersSponsorNoteCount}</p></div>
                </div>

                {allUsersSponsorNoteCount !== 0 && showSponsorAllUsersNotes && (
                    <div className='m-1 p-1 bg-gray-300 rounded'>
                      {sponsorAllUsersNotes && sponsorAllUsersNotes?.slice()
                          .sort((a, b) => new Date(b.sponsorAllUsersNotesId.sponsorAllUsersNoteUpdateDate) - new Date(a.sponsorAllUsersNotesId.sponsorAllUsersNoteUpdateDate)).map((note, noteIndex, arr) =>(
                        
                        <div key={note._id+ noteIndex} className='m-1 p-1 bg-white rounded'>
                            <div className='grid grid-cols-6 justify-between items-center m-1 p-1 bg-gray-100 rounded'>
                              
                              <div className='col-span-1 flex justify-start items-center'>
                                  <div
                                    onClick={() => handleToggleShowSponsorAllUsersNotesDetail(note._id)}
                                    className="mx-1 rounded-full h-6 w-6 hover:bg-white flex justify-center items-center cursor-pointer"
                                  >
                                    {showSponsorAllUsersNotesDetail[note._id] ? (
                                      <MinusIcon className="h-4 w-4 text-orange-600" />
                                    ) : (
                                      <PlusIcon className="h-4 w-4 text-orange-600" />
                                    )}
                                  </div>
                                  <div>
                                    {/* <p className='text-2xl font-bold mx-1 px-1 text-gray-600'>{arr.length - noteIndex}.</p> */}
                                    <p className='text-2xl font-bold mx-1 px-1 text-gray-600'>{noteIndex +1}.</p>
                                  </div>  
                                <div>
                                    <p className='text-[9px] mx-1 rounded-full px-2 py-[1px] bg-lime-200 w-18'>{getTimeSince(note?.sponsorAllUsersNotesId.sponsorAllUsersNoteUpdateDate)}</p>
                                    <p className='text-[7px] mx-1 italic px-2 py-1 text-gray-400'>last updated</p>
                                </div>  
                              </div>
                              <div className='col-span-3 flex justify-start items-center'>
                                <div dangerouslySetInnerHTML={createMarkup(note?.sponsorAllUsersNotesId.sponsorAllUsersNoteText)} className='text-[11px] text-gray-700 m-1 p-1 truncate max-w-full line-clamp-1'></div>
                              </div>
                              <div className='col-span-2 flex justify-start items-center mx-2'>
                                  <ClockIcon className='h-5 w-5 text-lime-600'/>
                                  <p className='text-[9px] mx-1'>First Created:</p>
                                  <p className='text-gray-400 text-[9px] mx-2'>{formatDateTime(note?.sponsorAllUsersNotesId.sponsorAllUsersNoteDate)}</p>
                              </div>
                            </div>
                            {/* 21 Jul 2026 */}
                            {showSponsorAllUsersNotesDetail[note._id] && (() => {
                              const noteTextSizeKey =
                                `sponsor-all-users-${note._id}`

                              return (
                                <div className="m-1 p-1 rounded bg-gray-50">
                                  {renderSessionNoteTextSizeSelector(
                                    noteTextSizeKey
                                  )}

                                  <div
                                    dangerouslySetInnerHTML={createMarkup(
                                      note?.sponsorAllUsersNotesId
                                        ?.sponsorAllUsersNoteText
                                    )}
                                    className={`fix-nested-lists ${getSessionNoteTextSizeClass(
                                      noteTextSizeKey
                                    )} text-gray-700 m-1 p-1`}
                                  />
                                </div>
                              )
                            })()}


                            {/* {showSponsorAllUsersNotesDetail[note._id] && (
                              <div className="m-1 p-1 rounded bg-gray-50">
                              <div
                                dangerouslySetInnerHTML={createMarkup(note?.sponsorAllUsersNotesId.sponsorAllUsersNoteText)}
                                className="fix-nested-lists text-[11px] text-gray-700 m-1 p-1"
                              ></div>
                              </div>
                            )} */}
                        </div>
                      ))}
                    </div>
                )}
                {/* When no sponsor notes are available */}
                {allUsersSponsorNoteCount === 0 && showSponsorAllUsersNotes && (
                  <div className="m-1 p-1 bg-white rounded">
                  <p className='text-orange-600 text-xs m-1 p-1'>No sponsor notes are available yet.</p>
                </div>
                )}
            </div>
            {/* Self User Sponsor Notes */}
            <div className='m-1 p-1 bg-gray-100 rounded'>
                
                <div className='m-1 p-1 flex justify-start items-center'>
                <div
                    className="flex justify-center items-center cursor-pointer rounded-full h-7 w-7 hover:bg-white m-1 p-1"
                    onClick={() => setShowSponsorSelfUserNotes((prev) => !prev)}
                  >
                    {showSponsorSelfUserNotes ? (
                      <MinusIcon className="h-5 w-5 text-orange-600" />
                    ) : (
                      <PlusIcon className="h-5 w-5 text-orange-600" />
                    )}
                  </div>
                  <div className='rounded-full w-7 h-7 flex justify-center items-center bg-gray-200 hover:bg-gray-50'><EtSponsorOneOnOneIcon1SVGComp className='w-7 h-7 text-orange-600'/></div>
                  <div><p className='text-sm text-gray-600 font-bold mx-1 px-1'>For Me:</p></div>
                  <div className='rounded-full flex justify-center items-center h-5 w-5 border border-orange-300 bg-gray-50 m-1'><p className='text-orange-600 text-[10px]'>{sharedUserSponsorNoteCount}</p></div>
                </div>

                {sharedUserSponsorNoteCount !== 0 && showSponsorSelfUserNotes && (
                    <div className='m-1 p-1 bg-gray-300 rounded'>
                      {sponsorSharedUserNotes && sponsorSharedUserNotes?.slice()
                          .sort((a, b) => new Date(b.sponsorSharedUserNotesId.sponsorSharedUserNoteUpdateDate) - new Date(a.sponsorSharedUserNotesId.sponsorSharedUserNoteUpdateDate)).map((note, noteIndex, arr) =>(
                        
                        <div key={note._id+ noteIndex} className='m-1 p-1 bg-white rounded'>
                            <div className='grid grid-cols-6 justify-between items-center m-1 p-1 bg-gray-100 rounded'>
                              
                              <div className='col-span-1 flex justify-start items-center'>
                                  <div
                                  onClick={() => handleToggleShowSponsorSelfUserNotesDetail(note._id)}
                                  className="mx-1 rounded-full h-6 w-6 hover:bg-white flex justify-center items-center cursor-pointer"
                                  >
                                  {showSponsorSelfUserNotesDetail[note._id] ? (
                                    <MinusIcon className="h-4 w-4 text-orange-600" />
                                  ) : (
                                    <PlusIcon className="h-4 w-4 text-orange-600" />
                                  )}
                                  </div>
                                  <div>
                                    {/* <p className='text-2xl font-bold mx-1 px-1 text-gray-600'>{arr.length - noteIndex}.</p> */}
                                    <p className='text-2xl font-bold mx-1 px-1 text-gray-600'>{noteIndex +1}.</p>
                                    </div> 
                                <div>
                                    <p className='text-[9px] mx-1 rounded-full px-2 py-[1px] bg-lime-200 w-18'>{getTimeSince(note?.sponsorSharedUserNotesId.sponsorSharedUserNoteUpdateDate)}</p>
                                    <p className='text-[7px] mx-1 italic px-2 py-1 text-gray-400'>last updated</p>
                                </div>  
                              </div>
                                <div className='col-span-3 flex justify-start items-center'>
                                <div dangerouslySetInnerHTML={createMarkup(note?.sponsorSharedUserNotesId.sponsorSharedUserNoteText)} className='text-[11px] text-gray-700 m-1 p-1 truncate max-w-full line-clamp-1'></div>
                                </div>
                                <div className='col-span-2 flex justify-start items-center mx-2'>
                                  <ClockIcon className='h-5 w-5 text-lime-600'/>
                                  <p className='text-[9px] mx-1'>First Created:</p>
                                  <p className='text-gray-400 text-[9px] mx-2'>{formatDateTime(note?.sponsorSharedUserNotesId.sponsorSharedUserNoteDate)}</p>
                                </div>
                            </div>
                            {/* 21 Jul 2026 */}
                            {showSponsorSelfUserNotesDetail[note._id] && (() => {
                              const noteTextSizeKey =
                                `sponsor-shared-user-${note._id}`

                              return (
                                <div className="m-1 p-1 rounded bg-gray-50">
                                  {renderSessionNoteTextSizeSelector(
                                    noteTextSizeKey
                                  )}

                                  <div
                                    dangerouslySetInnerHTML={createMarkup(
                                      note?.sponsorSharedUserNotesId
                                        ?.sponsorSharedUserNoteText
                                    )}
                                    className={`fix-nested-lists ${getSessionNoteTextSizeClass(
                                      noteTextSizeKey
                                    )} text-gray-700 m-1 p-1`}
                                  />
                                </div>
                              )
                            })()}


                            {/* {showSponsorSelfUserNotesDetail[note._id] && (
                              <div className="m-1 p-1 rounded bg-gray-50">
                              <div
                                dangerouslySetInnerHTML={createMarkup(note?.sponsorSharedUserNotesId.sponsorSharedUserNoteText)}
                                className="fix-nested-lists text-[11px] text-gray-700 m-1 p-1"
                              ></div>
                              </div>

                            )} */}
                        </div>
                      ))}
                    </div>
                )}
                {/* When no sponsor notes are available */}
                {sharedUserSponsorNoteCount === 0 && showSponsorSelfUserNotes && (
                  <div className="m-1 p-1 bg-white rounded">
                  <p className='text-orange-600 text-xs m-1 p-1'>No sponsor notes are available yet.</p>
                </div>
                )}
            </div>
          </div>
            )}    

      </div>

      {/* 04 Nov 2025 - Team notes */}
      <div className='m-1 p-1 border border-white rounded'>

        {/* Team Inputs Header Block*/}
        <div className='m-1 p-1 grid grid-cols-5 justify-start items-center'>
          <div className='col-span-2 flex justify-start items-center'>
              <div
                className="flex justify-center items-center cursor-pointer rounded-full h-7 w-7 hover:bg-white m-1 p-1"
                onClick={() => setShowSessionTeamNotes((prev) => !prev)}
              >
                {showSessionTeamNotes ? (
                  <MinusIcon className="h-5 w-5 text-orange-600" />
                ) : (
                  <PlusIcon className="h-5 w-5 text-orange-600" />
                )}
              </div>
            <div className='flex justify-center items-center cursor-pointer rounded-full h-7 w-7 bg-gray-200 hover:bg-gray-50 m-1 p-1'>
              <UserGroupIcon className="h-5 w-5 text-orange-600 hover:text-orange-600" />
            </div>
            <div className=''>
              <p className='font-bold mx-1'>Team Notes</p>
              <p className='text-[10px] italic text-gray-600 mx-1'>See what you and your team have to say... </p>
            </div>
          </div>
          
          <div className='col-span-1'>
            <div className='rounded-full flex justify-center items-center h-5 w-5 border border-orange-300 bg-gray-50 m-1'>
              <p className='text-orange-600 text-[10px]'>{teamNoteCount}</p>
            </div>
          </div>
        </div>

        {/* Team Note Details */}

        {showSessionTeamNotes && (
          <div className='m-1 p-1'>

            <div><p className='m-1 p-1 text-xs font-bold'>Team Notes Trail:</p></div>

            {
              introspectionSessionTeamNotes && 
              introspectionSessionTeamNotes.length >0 &&
              introspectionSessionTeamNotes
              ?.slice()
              .sort((a, b) => 
                new Date(b.originalIntrospectionSessionNoteId?.introspectionNoteUpdateDate) - 
                new Date(a.originalIntrospectionSessionNoteId?.introspectionNoteUpdateDate))
              .map((teamNote, teamNoteIndex) => {
                
                const name = teamNote?.noteSharedBy?.name || ""
                const username = teamNote?.noteSharedBy?.username || ""
                const noteText = teamNote?.originalIntrospectionSessionNoteId?.introspectionNoteText || ""
                const noteDate = teamNote?.originalIntrospectionSessionNoteId?.introspectionNoteDate || ""
                const noteUpdateDate = teamNote?.originalIntrospectionSessionNoteId?.introspectionNoteUpdateDate || ""
                
                return (
                  <div key={teamNote._id} className="bg-white p-1 m-1 rounded">

                    <div className='bg-gray-100 m-1 p-1 rounded '>   
                            <div className='grid grid-cols-10 justify-between items-center m-1 p-1'>
                                <div className='col-span-2 flex justify-start items-center'>
                                    <div
                                      onClick={() => handleToggleShowSessionTeamNotesDetail(teamNote._id)}
                                      className="mx-1 rounded-full h-6 w-6 hover:bg-white flex justify-center items-center cursor-pointer"
                                      >
                                        {showSessionTeamNotesDetail[teamNote._id] ? (
                                          <MinusIcon className="h-4 w-4 text-orange-600" />
                                        ) : (
                                          <PlusIcon className="h-4 w-4 text-orange-600" />
                                        )}
                                    </div>
                                    <div>
                                      <p className='text-2xl font-bold mx-1 px-1 text-gray-600'>{teamNoteIndex +1}.</p>
                                    </div>

                                    <div>
                                      <div className='m-1 p-1'>
                                        <User username={username} defaultUserPic={userProfileIcon2} />
                                      </div>
                                      <div className='w-16 my-1'>
                                        <p className='text-gray-600 text-[10px] text-center'>{name.split(" ")[0]}</p>
                                      </div>
                                    </div>

                                     <div>
                                        <p className='text-[9px] mx-1 rounded-full px-2 py-[1px] bg-lime-200 w-18'>{getTimeSince(noteUpdateDate)}</p>
                                        <p className='text-[7px] mx-1 italic px-2 py-1 text-gray-400'>last updated</p>
                                    </div>  
                                    
                                </div>

                                <div className='col-span-6 flex justify-start items-center'>
                                    <div dangerouslySetInnerHTML={createMarkup(noteText)} className="text-[11px] text-gray-700 m-1 p-1 truncate max-w-full line-clamp-1"></div>
                                </div>

                                <div className='col-span-2 flex justify-start items-center mx-2'>
                                    <ClockIcon className='h-5 w-5 text-lime-600'/>
                                    <p className='text-[9px] mx-1'>First Created:</p>
                                    <p className='text-gray-400 text-[9px] mx-2'>{formatDateTime(noteDate)}</p>

                                </div>
                            </div> 
                        </div>
                        
                        {/* 21 Jul 2026 */}
                        {showSessionTeamNotesDetail[teamNote._id] && (() => {
                          const noteTextSizeKey =
                            `team-note-${teamNote._id}`

                          return (
                            <div className="m-1 p-1 rounded bg-gray-50">
                              {renderSessionNoteTextSizeSelector(
                                noteTextSizeKey
                              )}

                              <div
                                dangerouslySetInnerHTML={createMarkup(
                                  noteText
                                )}
                                className={`fix-nested-lists ${getSessionNoteTextSizeClass(
                                  noteTextSizeKey
                                )} text-gray-700 m-1 p-1`}
                              />
                            </div>
                          )
                        })()}

                        {/* {showSessionTeamNotesDetail[teamNote._id] && (
                          <div className="m-1 p-1 rounded bg-gray-50">
                          <div
                            dangerouslySetInnerHTML={createMarkup(noteText)}
                            className="fix-nested-lists text-[11px] text-gray-700 m-1 p-1"
                          ></div>
                          </div>
                        )} */}
                  </div>
                )
              })}
            
          </div>
        )}

      </div>

      {/* 14 Jun 2024 Session Notes Updates - 22 Oct 2024*/}
      <div className='m-1 p-1 border border-white rounded'>
        <div className='m-1 p-1 grid grid-cols-5 justify-start items-center'>
          <div className='col-span-2 flex justify-start items-center'>
              <div
                className="flex justify-center items-center cursor-pointer rounded-full h-7 w-7 hover:bg-white m-1 p-1"
                onClick={() => setShowSessionNotes((prev) => !prev)}
              >
                {showSessionNotes ? (
                  <MinusIcon className="h-5 w-5 text-orange-600" />
                ) : (
                  <PlusIcon className="h-5 w-5 text-orange-600" />
                )}
              </div>
            <div className='flex justify-center items-center cursor-pointer rounded-full h-7 w-7 bg-gray-200 hover:bg-gray-50 m-1 p-1'>
              <DocumentTextIcon className="h-5 w-5 text-orange-600 hover:text-orange-600" />
            </div>
            <div className=''>
              <p className='font-bold mx-1'>Session Notes</p>
              <p className='text-[10px] italic text-gray-600 mx-1'>Capture your session experiences, thoughts and observations. </p>
            </div>
          </div>

          <div className='col-span-1'>
            <div className='rounded-full flex justify-center items-center h-5 w-5 border border-orange-300 bg-gray-50 m-1'>
                <p className='text-orange-600 text-[10px]'>{introspectionSessionNotes?.length}</p>
            </div>
          </div>
        </div>

        {showSessionNotes && (
          <div>

          {/* Add Button */}
          <div className="flex justify-start items-center m-1 p-1">
              <p className="font-bold text-xs m-1 p-1">Add</p>
              <button type="button" onClick={handleAddNote}>
              <PlusIcon className="h-5 w-5 text-white m-1 p-1 rounded bg-lime-600 hover:bg-green-600" />
              </button>
          </div>
          <div><p></p></div>

          {/* Session Notes Editor */}
          <div className='m-1 p-1'>
              {isEditing && (
                  <div>
                      <div className="bg-gray-50 p-1 m-1 rounded">
                          
                              <EtContentEditorLight2
                              value={editorValue}
                              handleChange={handleEditorChange}
                              placeholderText={editorValue}
                              // toolbarId={"sessionOurtro"}
                              /> 
                          
                      </div>
                      
                      {/* Save and Cancel Button */}
                      <div className="flex justify-start items-center m-1 p-1"> 
                                  <button type="button" onClick={handleSaveNote} className="w-16 text-white rounded-full bg-lime-600 hover:bg-lime-700">
                                      <p className="text-[10px] px-2 py-1">Save</p>
                                  </button>
                                  <button type="button" onClick={handleCancelEditNote} className="w-16 text-white rounded-full bg-orange-600 hover:bg-orange-700 mx-1">
                                      <p className="text-[10px] px-2 py-1">Cancel</p>
                                  </button>
                      </div>
                  </div>
              )}
          </div>

            {introspectionSessionNotes && introspectionSessionNotes.length >0 && (
            <div className='m-1 p-1'>
                <div><p className='m-1 p-1 text-xs font-bold'>Session Notes Trail:</p></div>
                <div>
                {introspectionSessionNotes
                ?.slice()
                .sort((a, b) => new Date(b.introspectionNoteUpdateDate) - new Date(a.introspectionNoteUpdateDate)) // Sort in descending order
                .map((note, noteIndex, arr) => (
                    <div key={note._id} className="bg-white p-1 m-1 rounded">
                        <div className='bg-gray-100 m-1 p-1 rounded '>   
                            <div className='grid grid-cols-7 justify-between items-center m-1 p-1'>
                                <div className='col-span-1 flex justify-start items-center'>
                                <div
                                      onClick={() => handleToggleShowSessionNotesDetail(note._id)}
                                      className="mx-1 rounded-full h-6 w-6 hover:bg-white flex justify-center items-center cursor-pointer"
                                      >
                                    {showSessionNotesDetail[note._id] ? (
                                      <MinusIcon className="h-4 w-4 text-orange-600" />
                                    ) : (
                                      <PlusIcon className="h-4 w-4 text-orange-600" />
                                    )}
                                    </div>
                                    <div>
                                      {/* <p className='text-2xl font-bold mx-1 px-1 text-gray-600'>{arr.length - noteIndex}.</p> */}
                                      <p className='text-2xl font-bold mx-1 px-1 text-gray-600'>{noteIndex +1}.</p>
                                      </div>
                                    <div>
                                        <p className='text-[9px] mx-1 rounded-full px-2 py-[1px] bg-lime-200 w-18'>{getTimeSince(note?.introspectionNoteUpdateDate)}</p>
                                        <p className='text-[7px] mx-1 italic px-2 py-1 text-gray-400'>last updated</p>
                                    </div>  
                                </div>

                                <div className='col-span-3 flex justify-start items-center'>
                                    <div dangerouslySetInnerHTML={createMarkup(note?.introspectionNoteText)} className="text-[11px] text-gray-700 m-1 p-1 truncate max-w-full line-clamp-1"></div>
                                </div>

                                <div className='col-span-2 flex justify-start items-center mx-2'>
                                    <ClockIcon className='h-5 w-5 text-lime-600'/>
                                    <p className='text-[9px] mx-1'>First Created:</p>
                                    <p className='text-gray-400 text-[9px] mx-2'>{formatDateTime(note.introspectionNoteDate)}</p>

                                </div>
                                
                                <div className='col-span-1 flex justify-end items-center mx-2'>
                                    <div className='rounded-full flex justify-center items-center h-6 w-6 hover:bg-white'>
                                        <PencilSquareIcon 
                                            className="h-4 w-4 p-[2px] text-orange-600 cursor-pointer" 
                                            onClick={() => handleEditNote(note)}
                                        />
                                    </div>
                                    {/* 04 Nov 2025 */}
                                    <div className='rounded-full flex justify-center items-center h-6 w-6 hover:bg-white'>
                                      <ShareIcon
                                        className="h-4 w-4 p-[2px] text-orange-600 cursor-pointer"
                                        // onClick={() => handleShareNote(note)}
                                        onClick={() => {
                                          setNoteToShare(note)
                                          setShowShareConfirmModal(true)
                                        }}
                                      />
                                    </div>

                                    <div className='rounded-full flex justify-center items-center h-6 w-6 hover:bg-white'>
                                        <TrashIcon 
                                            className="h-4 w-4 p-[2px] text-orange-600 cursor-pointer"
                                            // onClick={() => handleDeleteNote(note._id)} 
                                            onClick={() => {
                                              if (note.sharedWith && note.sharedWith.length > 0) {
                                                alert("This note has already been shared and cannot be deleted.");
                                                return;
                                              }
                                              setNoteToDelete(note);
                                              setShowDeleteConfirmModal(true);
                                            }} 
                                        />
                                    </div>
                                </div>
                            </div> 
                        </div>

                        {/* 14 Sep 2024 - Refined the details display - shifted here */}

                        {/* 21 Jul 2026 */}
                        {showSessionNotesDetail[note._id] && (() => {
                          const noteTextSizeKey =
                            `user-session-note-${note._id}`

                          return (
                            <div className="m-1 p-1 rounded bg-gray-50">
                              {renderSessionNoteTextSizeSelector(
                                noteTextSizeKey
                              )}

                              <div
                                dangerouslySetInnerHTML={createMarkup(
                                  note?.introspectionNoteText
                                )}
                                className={`fix-nested-lists ${getSessionNoteTextSizeClass(
                                  noteTextSizeKey
                                )} text-gray-700 m-1 p-1`}
                              />
                            </div>
                          )
                        })()}


                        {/* {showSessionNotesDetail[note._id] && (
                          <div className="m-1 p-1 rounded bg-gray-50">
                          <div
                            dangerouslySetInnerHTML={createMarkup(note?.introspectionNoteText)}
                            className="fix-nested-lists text-[11px] text-gray-700 m-1 p-1"
                          ></div>
                          </div>
                        )} */}
                    </div>
                ))}
                </div>
            </div>
            )}
            </div>
        )}

        {/* 05 Nov 2025 */}
        <ModalShareConfirm
          isOpen={showShareConfirmModal}
          onClose={() => {
            setShowShareConfirmModal(false)
            setNoteToShare(null)
          }}
          onConfirm={() => {
            if (noteToShare) {
              handleShareNote(noteToShare)
              setShowShareConfirmModal(false)
              setNoteToShare(null)
            }
          }}
        />
        {/* 05 Nov 2025 */}
        <Modal
          isOpen={showDeleteConfirmModal}
          onClose={() => {
            setShowDeleteConfirmModal(false);
            setNoteToDelete(null);
          }}
          onConfirm={() => {
            if (noteToDelete) {
              handleDeleteNote(noteToDelete._id);
              setShowDeleteConfirmModal(false);
              setNoteToDelete(null);
            }
          }}
        />


      </div>

      {/* Session Actions Section - 22 Oct 2024 */}
      <div className='m-1 p-1 border border-white rounded'>
      <div className='m-1 p-1'>
        <div className=''>
          <div className='grid grid-cols-5 justify-start items-center'>
                <div className='col-span-2 flex justify-start items-center'>
                    <div
                    className="flex justify-center items-center cursor-pointer rounded-full h-7 w-7 hover:bg-white m-1 p-1"
                    onClick={() => setShowSessionActions((prev) => !prev)}
                  >
                    {showSessionActions ? (
                      <MinusIcon className="h-5 w-5 text-orange-600" />
                    ) : (
                      <PlusIcon className="h-5 w-5 text-orange-600" />
                    )}
                  </div>
                  <div className='rounded-full flex justify-center items-center h-7 w-7 bg-gray-200 hover:bg-white m-1 p-1'>
                    <CursorArrowRippleIcon className="h-5 w-5 text-orange-600" />
                    </div>
                  <div>
                      <p className="font-bold mx-1 px-1">Actions</p>
                      <p className="text-[10px] mx-1 px-1 italic text-gray-600">What would you like to light up next?</p>
                  </div>
                </div>

                <div className='col-span-1'>
                    <div className='rounded-full flex justify-center items-center h-5 w-5 border border-orange-300 bg-gray-50 m-1'>
                      <p className='text-orange-600 text-[10px]'>{userActionsCount}</p>
                    </div>
                </div>
          </div>
        </div> 

        {showSessionActions && ( 
            <div>         
              <div>
              {/* Component to display the trail session actions for the user */}
              {/* <EtUserActionListComp userId={userId} trailId={trailId} sessionId={sessionId}/> */}
              {/* <EtUserActionListByGroupComp userId={userId} trailId={trailId} sessionId={sessionId}/> */}
              <EtUserActionListByGroupWithTabsComp userId={userId} trailId={trailId} sessionId={sessionId}/>
              </div>
              <div className="flex justify-start items-center m-1 p-1">
                  <p className="font-bold text-xs m-1 p-1">Add</p>
                  <button type="button" onClick={handleAddAction}>
                    <PlusIcon className="h-5 w-5 text-white m-1 p-1 rounded bg-lime-600 hover:bg-green-600" />
                  </button>
              </div>
            </div>
          )}   

      </div>
      </div>

      {/* Session Action Bar */}
      <div id="actions" className="bg-white m-1 px-1 py-3 rounded">
              <EtBtnFormBasic
                name={"Save"}
                type={"create"}
                handleClick={handleSave}
              />
              <EtBtnFormBasic
                name={"Clear"}
                type={"clear"}
                handleClick={handleClear}
              />
              <EtBtnFormBasic
                name={"Cancel"}
                type={"cancel"}
                handleClick={handleCancel}
              />
      </div>
    </div>
  )

}

const EtSessionChallengesSection = ({
  sessionChallenges, 
  userId, 
  trailId, 
  sessionId, 
  userOrgId, 
  selfUserData,
  cohortId,   // 04 Jul 2026
}) => {
  // First Created - 21 Aug 2024 
  // Author - Atul Pandey
  // Purpose - to simplify and decouple the different section JSXs from the main display component
  // Updated - 23 Aug 2024 - triggerUrl to capture and pass the url triggering the modal for it to navigate back to that page upon completion
  // Updated - 23 Aug 2024 - user challenge self eval scores 
  // Updated - 23 Aug 2024 - get color scores to depict scores as Et Pixels - PCI Luminance
  // Updated - 24 Aug 2024 - Evaluation bar header - Dimension, Self Eval, Sponsor Eval
  // Updated - 24 Aug 2024 - Sponsor Evaluation
  // Updated - 25 Aug 2024 - Added Sponsor User Challenge Evaluations
  // Updated - 28 Aug 2024 - Refined display of coach, sponsor, self eval data
  // Updated - 29 Aug 2024 - Refined display of coach, sponsor evals with memoized data
  // Updated - 30 Aug 2024 - Cleaned up code. Added section for user, coach and sponsor eval comments (challangeDimEvalDetails)
  // Updated - 31 Aug 2024 - Added user, coach and sponsor evaluation comments
  // Updated - 31 Aug 2024 - Added the Modal to Add or Update User Challenge Self Eval
  // Updated - 14 Sep 2024 - Refined the header JSX - from flex to grid
  // Updated - 04 Jul 2026 - Refined the header JSX - from flex to grid
  
  // 1a - State variables for display navigation
  const [showChallengeDetails, setShowChallengeDetails] = useState(false) // 19 Aug 2024
  const [showChallengeSections, setShowChallengeSections] = useState({}) // 19 Aug 2024
  const [showInstructionDetails, setShowInstructionDetails] = useState({}) // 19 Aug 2024
  const [showChallengeDimDetails, setShowChallengeDimDetails] = useState({}) // 19 Aug 2024
  const [showChallengeDimEvalDetails, setShowChallengeDimEvalDetails] = useState({}) // 30 Aug 2024
  const [showChallengeHeadDetails, setShowChallengeHeadDetails] = useState({}) // 21 Aug 2024
  const [existingEvalData, setExistingEvalData] = useState(null) // 31 Aug 2024 - to allow create / update of self user challenge evals
  const [mode, setMode] = useState("create") // 31 Aug 2024 - to allow create / update of self user challenge evals


  // 1b - Extract user challenge self eval data using RTK query - 23 Aug 2024
  const { data: userChallengeSelfEvalData, refetch} = useGetUserChallengeSelfEvalsQuery()
  const userChallengeSelfEvals = userChallengeSelfEvalData?.ids?.map((id) => {
    const userChallengeSelfEval = userChallengeSelfEvalData?.entities[id]
    return userChallengeSelfEval
  })
  // console.log('User Challenge Self Evals', userChallengeSelfEvals)
  // console.log('Session Challenges', sessionChallenges)
  // console.log('SessionHeads:', sessionChallenges?.map(challenge => challenge.challengeDimensions.map(dimension => dimension.challengeDimId.challengeHeadId.challengeHeadName
  // )))
  // console.log('SelfUserData:', selfUserData)

  // 1c - Filter the challenge eval data for the given user org, trail and session - 23 Aug 2024
  const filteredUserChallengeSelfEvals = userChallengeSelfEvals?.filter(evalItem =>
    evalItem?.userId?._id === userId &&
    evalItem?.userId?.userOrgId === userOrgId &&
    evalItem?.trailId?._id === trailId &&
    evalItem?.sessionId?._id === sessionId &&
    evalItem?.cohortId?._id === cohortId &&     // 04 Jul 2026

    sessionChallenges.some(challenge => challenge._id === evalItem.challengeId)
  )
  // console.log('Filtered User Challenge Self Evals:', filteredUserChallengeSelfEvals)

  // 1d - Extract coach user challenge evaluation data - 24 Aug 2024
  const {data: userChallengeCoachEvalData} = useGetUserChallengeCoachEvalsQuery()
  const userChallengeCoachEvals = userChallengeCoachEvalData?.ids?.map((id) => {
    const userChallengeCoachEval = userChallengeCoachEvalData?.entities[id]
    return userChallengeCoachEval
  })
  // console.log('User Challenge Coach Evals:', userChallengeCoachEvals)

  // 1e - Filter the coach challenge eval data for the given user org, trail and session - 24 Aug 2024
  const filteredUserChallengeCoachEvals = userChallengeCoachEvals?.filter(evalItem =>
    evalItem?.userId?._id === userId &&
    evalItem?.userId?.userOrgId === userOrgId &&
    evalItem?.trailId?._id === trailId &&
    evalItem?.sessionId?._id === sessionId &&
    sessionChallenges?.some(challenge => challenge._id === evalItem?.challengeId)
  )
  // console.log('Filtered User Challenge Coach Evals:', filteredUserChallengeCoachEvals)

  // 1f - Extract coach user challenge evaluation data - 25 Aug 2024
  const {data: userChallengeSponsorEvalData} = useGetUserChallengeSponsorEvalsQuery()
  const userChallengeSponsorEvals = userChallengeSponsorEvalData?.ids?.map((id) => {
    const userChallengeSponsorEval = userChallengeSponsorEvalData?.entities[id]
    return userChallengeSponsorEval
  })
  // console.log('User Challenge Sponsor Evals:', userChallengeSponsorEvals)

  // 1g - Filter the sponsor challenge eval data for the given user org, trail and session - 24 Aug 2024
  const filteredUserChallengeSponsorEvals = userChallengeSponsorEvals?.filter(evalItem =>
    evalItem?.userId?._id === userId &&
    evalItem?.userId?.userOrgId === userOrgId &&
    evalItem?.trailId?._id === trailId &&
    evalItem?.sessionId?._id === sessionId &&
    sessionChallenges.some(challenge => challenge._id === evalItem.challengeId)
  )
  // console.log('Filtered User Challenge Sponsor Evals:', filteredUserChallengeSponsorEvals)

  // 1h - State for Modal -- 11 Jun 2024
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [triggeringUrl, setTriggeringUrl] = useState(null); // 23 Aug 2024

  // 1i - Handle Evaluate Action to activate modal - 23 Aug 2024
  const handleEvaluate = (challengeId) => {
    // handleOpenChallengeEvalModal()
    const currentUrl = window.location.pathname; // 23 Aug 2024 - Capture the current URL
    setTriggeringUrl(currentUrl); // 23 Aug 2024 - Set the triggering URL

     // 31 Aug 2024 - Check if there's an existing evaluation for the selected challenge and user
    //  const existingEval = filteredUserChallengeSelfEvals?.find(
    //   (evalItem) =>
    //     evalItem.sessionChallengeCoachEvalId.challengeId === challengeId &&
    //     evalItem.sessionChallengeCoachEvalId.userId === userId
    // )
    const existingEval = filteredUserChallengeSelfEvals?.find(evalItem =>
      evalItem.challengeId === challengeId 
      // && (evalItem.cohortId === cohortId  || evalItem.cohortId?._id === cohortId )        // 04 Jul 2026
     )
  
    setIsModalOpen(true)
    // Determine the mode and set existing evaluation data if it exists
    const mode = existingEval ? "update" : "create"
    setMode(mode)
    setExistingEvalData(
      existingEval ? existingEval : null
    )
  }
  
  // // 1j -  Modal management functions for Challenge Evaluations - 22 Aug 2024
  // const handleOpenChallengeEvalModal = () => {
  //   const currentUrl = window.location.pathname; // 23 Aug 2024 - Capture the current URL
  //   setTriggeringUrl(currentUrl); // 23 Aug 2024 - Set the triggering URL
  //   setIsModalOpen(true)
  // }

  const handleCloseChallengeEvalModal = () => {
    setIsModalOpen(false)
    setTriggeringUrl(null); // 23 Aug 2024 Reset the triggering URL
    refetch()
  }
  
  // 2a - Toggle Show function for challenge instruction details
  const handleToggleChallengeSections = (challengeId) => {
    setShowChallengeSections(prev => ({ ...prev, [challengeId]: !prev[challengeId] }));
  };

  // 2b - Toggle Show function for challenge instruction details
  const handleToggleInstructionDetails = (challengeId) => {
    setShowInstructionDetails(prev => ({ ...prev, [challengeId]: !prev[challengeId] }));
  };

  // 2c - Toggle Show function for challenge dimension details
  const handleToggleChallengeHeadDetails = (challengeId) => {
    setShowChallengeHeadDetails(prev => ({ ...prev, [challengeId]: !prev[challengeId] }));
  };

   // 2d - Toggle Show function for challenge dimension details
   const handleToggleChallengeDimDetails = (challengeId) => {
    setShowChallengeDimDetails(prev => ({ ...prev, [challengeId]: !prev[challengeId] }));
  };

   // 2e - Toggle Show function for challenge dimension eval details - 30 Aug 2024
   const handleToggleChallengeDimEvalDetails = (challengeId) => {
    setShowChallengeDimEvalDetails(prev => ({ ...prev, [challengeId]: !prev[challengeId] }));
  };

  // 3 - Calculate the number of session challenges - 19 Aug 2024
  const challengeCount = sessionChallenges?.length || 0
  // console.log('challengeCount :', challengeCount)

  // 4 - Function to get the appropriate color class based on the eval score - 23 Aug 2024
  const getColorForScore = (score) => {
    if (score >= 0 && score < 0.9) return "bg-etpx-0"
    if (score >= 0.9 && score < 1.9) return "bg-etpx-1"
    if (score >= 1.9 && score < 2.9) return "bg-etpx-2"
    if (score >= 2.9 && score < 3.9) return "bg-etpx-3"
    if (score >= 3.9 && score < 4.9) return "bg-etpx-4"
    if (score >= 4.9) return "bg-etpx-5" // For score >= 4.9
    return "bg-black" // default
  }

  // 5 -  Extract Unique Coaches from filteredUserChallengeCoachEvals - 28 Aug 2024 
  const sessionCoaches = filteredUserChallengeCoachEvals?.reduce((acc, evalItem) => {
    const coachId = evalItem.coachId?._id;
    // Check if this coach is already added to the list
    if (coachId && !acc.some(coach => coach._id === coachId)) {
      acc.push(evalItem.coachId); // Add coach to the list if not already present
    }
    return acc;
  }, []);
  // console.log('Session Coaches', sessionCoaches)

  // 6 -  Extract Unique Coaches from filteredUserChallengeCoachEvals - 28 Aug 2024 
  const sessionSponsors = filteredUserChallengeSponsorEvals?.reduce((acc, evalItem) => {
    const sponsorId = evalItem.sponsorId?._id;
    // Check if this sponsor is already added to the list
    if (sponsorId && !acc.some(sponsor => sponsor._id === sponsorId)) {
      acc.push(evalItem.sponsorId); // Add sponsor to the list if not already present
    }
    return acc;
  }, []);
  // console.log('Session Sponsors', sessionSponsors)

  // 7 - Helper function to determine if a dimension should be skipped - 28 Aug 2024, -- 29 Aug 2024 - modified - added  evaluationType, sessionUsers
  const shouldSkipDimension = (dimension, matchingEval, dimensions, currentUser, evaluationType) => {
    if (!matchingEval) {
      // console.log(`No matching evaluation found for ${currentUser.name}`);
      return true; // Skip if no matching evaluation
    }
  
    const challengeHeadId = dimension.challengeDimId.challengeHeadId._id;
    const dimensionsInHead = dimensions.filter(dim => 
      dim.challengeDimId.challengeHeadId._id === challengeHeadId
    );
  
    const evalField = evaluationType === 'coach' ? 'userChallengeCoachEvals' : 'userChallengeSponsorEvals';
  
    const hasEvaluationsForHead = matchingEval[evalField]?.some(evaluation =>
      dimensionsInHead.some(dim => evaluation.userChallengeDimId === dim.challengeDimId._id)
    );
  
    // console.log(`Should skip for ${currentUser.name}: ${!hasEvaluationsForHead}`);
    return !hasEvaluationsForHead;
  };
  
  // 8 - Memoized Evaluations - 29 Aug 2024
  const memoizedEvaluations = useMemo(() => {
    return sessionChallenges?.reduce((acc, challenge) => {
      challenge.challengeDimensions?.forEach(dimension => {
        const headName = dimension.challengeDimId?.challengeHeadId?.challengeHeadName || "Unknown Head";
        if (!acc[challenge._id]) acc[challenge._id] = {};
        if (!acc[challenge._id][headName]) {
          acc[challenge._id][headName] = {
            coaches: sessionCoaches?.map((coach) => {
              const matchingCoachEval = filteredUserChallengeCoachEvals?.find(evalItem => 
                evalItem.coachId._id === coach._id && 
                evalItem.challengeId === challenge._id
              );
              return { coach, matchingCoachEval };
            }),
            sponsors: sessionSponsors?.map((sponsor) => {
              const matchingSponsorEval = filteredUserChallengeSponsorEvals?.find(evalItem => 
                evalItem.sponsorId._id === sponsor._id && 
                evalItem.challengeId === challenge._id
              );
              return { sponsor, matchingSponsorEval };
            })
          };
        }
      });
      return acc;
    }, {});
  }, [sessionChallenges, sessionCoaches, sessionSponsors, filteredUserChallengeCoachEvals, filteredUserChallengeSponsorEvals]);
  
  // Console logs for debugging 
  // console.log('filteredUserChallengeCoachEvals:', filteredUserChallengeCoachEvals);
  // console.log('sessionCoaches:', sessionCoaches);
  // console.log('sessionSponsors:', sessionSponsors);
  // console.log('memoizedEvaluations:', memoizedEvaluations);
  
  
  return(
    <div>
      {challengeCount > 0 && (
      <div className='m-1 p-1 border border-white rounded'>
        {/* Challenge Header Block - 14 Sep 2024 - Changed from flex to grid*/}
        <div className='m-1 p-1 grid grid-cols-5 justify-start items-center'>
          <div className='col-span-2 flex justify-start items-center'>
            <div
              className="flex justify-center items-center cursor-pointer rounded-full h-7 w-7 hover:bg-white m-1 p-1"
              onClick={() => setShowChallengeDetails((prev) => !prev)}
            >
              {showChallengeDetails ? (
                <MinusIcon className="h-5 w-5 text-orange-600" />
              ) : (
                <PlusIcon className="h-5 w-5 text-orange-600" />
              )}
            </div>
            <div className='rounded-full w-7 h-7 flex justify-center items-center bg-gray-200 hover:bg-gray-50  m-1 p-1'><EtChallengeIcon1SVGComp className='w-5 h-5 text-orange-600'/></div>
            <div>
              <p className='font-bold mx-1 px-1'>Session Challenges </p>
              <p className='text-[10px] italic text-gray-600 mx-1'>Test your client understanding muscle.. </p>
            </div>
          </div>

          <div className='col-span-1'>
            <div className='rounded-full flex justify-center items-center h-5 w-5 border border-orange-300 bg-gray-50 m-1'>
              <p className='text-orange-600 text-[10px]'>{challengeCount}</p>
            </div>
          </div>

        </div>
        {showChallengeDetails && (
          <div>
           {sessionChallenges && sessionChallenges?.length > 0 && sessionChallenges?.map((challenge, challengeIndex) =>{
            return(
              <div key={challenge._id+challengeIndex} className='m-1 p-1 bg-gray-100 rounded'>
                <div className='flex justify-between items-center m-1 p-1'>
                  <div className='flex justify-start items-center'>
                      <div className='m-1 p-1 cursor-pointer rounded-full h-6 w-6 flex justify-center items-center hover:bg-white' onClick={() => handleToggleChallengeSections(challenge._id)}>
                          {showChallengeSections[challenge._id] ? (
                              <MinusIcon className="h-4 w-4 text-orange-600" />
                          ) : (
                              <PlusIcon className="h-4 w-4 text-orange-600" />
                          )}
                      </div>
                    <div>
                      <p className='font-bold'>{challenge.challengeName}</p>
                      <p className='text-[10px] italic'>{challenge.challengeDesc}</p>
                    </div>
                  </div>
                </div>

                {showChallengeSections[challenge._id] && (
                  <div>
                      {/* Section - Challenge Instructions */}
                      <div className='mx-1 px-1'>
                        <div className='bg-gray-200 rounded'>
                          <div className='flex justify-between items-center m-1 p-1'>
                              <div className='flex justify-start items-center m-1 p-1'>
                                <div className='m-1 p-1 cursor-pointer rounded-full h-6 w-6 flex justify-center items-center hover:bg-white' onClick={() => handleToggleInstructionDetails(challenge._id)}>
                                      {showInstructionDetails[challenge._id] ? (
                                          <MinusIcon className="h-4 w-4 text-orange-600" />
                                      ) : (
                                          <PlusIcon className="h-4 w-4 text-orange-600" />
                                      )}
                                  </div>
                                  <div className='rounded-full h-7 w-7 flex justify-center items-center hover:bg-white'><WrenchIcon className='h-6 w-6 text-orange-600'/></div>
                                  <div><p className='mx-1 px-1 text-gray-500 font-bold'>Instructions</p></div>
                              </div>
                          </div>
                          {showInstructionDetails[challenge._id] && challenge.challengeInstructions?.length >0 && (
                              <div className='m-1 p-1 rounded'>
                                <div className='m-1 p-1 rounded bg-gray-300'>
                                  {challenge.challengeInstructions?.map((instruction, insIndex) =>{
                                    return(
                                      <div key={instruction._id + insIndex} className='bg-gray-100 m-1 p-1 rounded'>
                                        <div className='flex justify-start items-center m-1 p-1'>
                                            <div><p className='text-gray-400 font-bold mx-1 px-1'>{insIndex +1}.</p></div>
                                            <div>
                                                <div><p className='text-[10px] mx-1 px-1'><span className=''>{instruction.instruction}</span> </p></div>
                                            </div>
                                        </div>   
                                      </div>
                                    )
                                  })}
                                </div>
                              </div>
                            )}  
                        
                        </div>
                      </div>

                      {/* Section - Challenge Dimensions */}
                      <div className='mx-1 px-1'>
                        <div className='bg-gray-200 rounded'>
                          <div className='flex justify-between items-center m-1 p-1'>
                              <div className='flex justify-start items-center m-1 p-1'>
                                <div className='m-1 p-1 cursor-pointer rounded-full h-6 w-6 flex justify-center items-center hover:bg-white' onClick={() => handleToggleChallengeHeadDetails(challenge._id)}>
                                      {showChallengeHeadDetails[challenge._id] ? (
                                          <MinusIcon className="h-4 w-4 text-orange-600" />
                                      ) : (
                                          <PlusIcon className="h-4 w-4 text-orange-600" />
                                      )}
                                  </div>
                                  <div className='rounded-full h-7 w-7 flex justify-center items-center hover:bg-white'><SunIcon className='h-6 w-6 text-orange-600'/></div>
                                  <div><p className='mx-1 px-1 text-gray-500 font-bold'>Dimensions</p></div>
                              </div>
                          </div>
                        {showChallengeHeadDetails[challenge._id] && challenge.challengeDimensions?.length > 0 && (
                          <div className=''>
                            <div className='m-1 p-1 rounded bg-gray-200'>
                              {/* Step 1: Group the dimensions by challengeHeadName */}
                              {Object.entries(
                                challenge.challengeDimensions?.reduce((acc, dimension) => {
                                  const headName = dimension.challengeDimId?.challengeHeadId?.challengeHeadName || "Unknown Head";
                                  if (!acc[headName]) acc[headName] = [];
                                  acc[headName].push(dimension);
                                  return acc;
                                }, {})
                              ).map(([headName, dimensions], headIndex) => {

                                // console.log('headname: ',headName, ' dimensions length:',dimensions.length)
                                // console.log('dimensions', dimensions)
                                
                                // 28 Aug 2024
                                const hasUserEvals = filteredUserChallengeSelfEvals?.some(evalItem =>
                                  evalItem.userChallengeEvals?.some(evaluation =>
                                    dimensions.some(dimension =>
                                      evaluation.userChallengeDimId === dimension.challengeDimId._id
                                    )
                                  )
                                );

                                return(
                                <div key={headName + headIndex+ challenge._id} className='bg-white m-1 p-1 rounded'>
                                  {/* Step 2: Display the challengeHeadName and the evaluators when details are clicked*/}
                                      <div className=''>
                                        <div className='grid grid-cols-12 justify-between items-center m-1 p-1'>
                                          <div className='col-span-4 flex justify-start items-center m-1 p-1'>
                                            <div className='m-1 p-1 cursor-pointer rounded-full h-6 w-6 flex justify-center items-center hover:bg-white' onClick={() => handleToggleChallengeDimDetails(headName)}>
                                                {showChallengeDimDetails[headName] ? (
                                                    <MinusIcon className="h-4 w-4 text-orange-600" />
                                                ) : (
                                                    <PlusIcon className="h-4 w-4 text-orange-600" />
                                                )}
                                            </div>
                                              <div>
                                                <div><p className='mx-1 px-1 font-bold'>{headName}</p></div>
                                                <div onClick={() =>handleEvaluate(challenge._id)} className='cursor-pointer'><p className='mx-1 px-1 text-[10px] text-orange-600'>Evaluate</p></div>
                                              </div>                                                  
                                          </div>

                                
                                          {/* User Header Bar - to display each user who has done evaluation */}
                                          {showChallengeDimDetails[headName] && (
                                            <div className='col-span-8 gap-2 flex justify-end items-center'>

                                              {/* Display Self if there are any evaluations */}
                                               {/* 28 Aug 2024 Check if there are any self evaluations */}
                                                {hasUserEvals && (
                                                  <div className='flex flex-col justify-center items-center m-1 p-1'>
                                                    <div className='w-16'>
                                                      <p className='text-orange-600 text-[9px] text-center'>You</p>
                                                    </div>
                                                    <div className='m-1 p-1'>
                                                      <User username={selfUserData.username} defaultUserPic={userProfileIcon2} />
                                                    </div>
                                                    <div className='w-16 my-1'>
                                                      <p className='text-gray-600 text-[10px] text-center'>{selfUserData.name.split(" ")[0]}</p>
                                                    </div>
                                                  </div>
                                                )}
                                              
                                                  {/* Display each coach user if they have any evaluations */}
                                                  {sessionCoaches?.map((coach, coachIndex) => {

                                                  const matchingCoachEvals = filteredUserChallengeCoachEvals?.filter(evalItem =>
                                                    evalItem.userChallengeCoachEvals?.some(evaluation => 
                                                      dimensions.some(dimension =>
                                                        evaluation.userChallengeDimId === dimension.challengeDimId._id
                                                      )
                                                    )
                                                  );

                                                    // console.log('maching coach evals --indise display session coaches', matchingCoachEvals)
                                                    const coachEvals = matchingCoachEvals?.filter(evalItem => evalItem?.coachId._id === coach._id);
                                                    const hasCoachEval = coachEvals?.length > 0;

                                                    if (!hasCoachEval) return null; // Skip displaying if there are no evaluations for the coach

                                                    return (
                                                      <div key={coach._id + coachIndex} className='flex flex-col justify-center items-center m-1 p-1'>
                                                        <div className='w-16'>
                                                          <p className='text-orange-600 text-[9px] text-center'>Coach</p>
                                                        </div>
                                                        <div className='m-1 p-1'>
                                                          <User username={coach.username} defaultUserPic={userProfileIcon2} />
                                                        </div>
                                                        <div className='w-16 my-1'>
                                                          <p className='text-gray-600 text-[10px] text-center'>{coach.name.split(" ")[0]}</p>
                                                        </div>
                                                      </div>
                                                    );
                                                  })}

                                                      {/* Display each sponsor user */}
                                                      {sessionSponsors?.map((sponsor, sponsorIndex) => {

                                                      const matchingSponsorEvals = filteredUserChallengeSponsorEvals?.filter(evalItem =>
                                                        evalItem.userChallengeSponsorEvals?.some(evaluation => 
                                                          dimensions.some(dimension =>
                                                            evaluation.userChallengeDimId === dimension.challengeDimId._id
                                                          )
                                                        )
                                                      );

                                                        // console.log('maching sponsor evals --indise display session sponsores', matchingSponsorEvals)
                                                        const sponsorEvals = matchingSponsorEvals?.filter(evalItem => evalItem?.sponsorId._id === sponsor._id);
                                                        const hasSponsorEval = sponsorEvals?.length > 0;

                                                        if (!hasSponsorEval) return null; // Skip displaying if there are no evaluations for the sponsor

                                                        return (
                                                          <div key={sponsor._id + sponsorIndex} className='flex flex-col justify-center items-center m-1 p-1'>
                                                            <div className='w-16'>
                                                              <p className='text-orange-600 text-[9px] text-center'>Sponsor</p>
                                                            </div>
                                                            <div className='m-1 p-1'>
                                                              <User username={sponsor.username} defaultUserPic={userProfileIcon2} />
                                                            </div>
                                                            <div className='w-16 my-1'>
                                                              <p className='text-gray-600 text-[10px] text-center'>{sponsor.name.split(" ")[0]}</p>
                                                            </div>
                                                          </div>
                                                        );
                                                      })}
                                                </div>
                                            )}
                                        </div>                                        
                                      </div>

                                  {/* Step 3: Display each dimension under the challengeHeadName */}
                                  {showChallengeDimDetails[headName] && dimensions.map((dimension, dimIndex) => {

                                    // 28 Aug 2024 Find the matching evaluation for the current dimension
                                        const matchingEval = filteredUserChallengeSelfEvals?.find(evalItem =>
                                          evalItem.userChallengeEvals?.some(evaluation => evaluation.userChallengeDimId === dimension.challengeDimId._id)
                                      );
                                      // console.log('matchingEval', matchingEval)

                                      // 31 Aug 2024 - Extract the exact matching dim evaluation
                                      const matchingDimEval = filteredUserChallengeSelfEvals?.find(evalItem =>
                                        evalItem.userChallengeEvals?.find(evaluation => evaluation.userChallengeDimId === dimension.challengeDimId._id)
                                    );
                                    // console.log('matchingDimEval', matchingDimEval)
                                      

                                      // 28 Aug 2024 - Extract the userChallengeDimSelfScore if a matching evaluation is found
                                      const userEvalScore = matchingEval?.userChallengeEvals?.find(evaluation => evaluation.userChallengeDimId === dimension.challengeDimId._id)?.userChallengeDimSelfScore || "N/A";

                                      const displayUserEvalScore = (userEvalScore !== undefined && userEvalScore !== null) ? userEvalScore : "N/A";

                                      // 31 Aug 2024 - Extract the userChallengeDimSelfEvalText if a matching evaluation is found
                                      const userEvalText = matchingEval?.userChallengeEvals?.find(evaluation => evaluation.userChallengeDimId === dimension.challengeDimId._id)?.userChallengeDimSelfEvalText
                                      || "N/A";

                                      // 31 Aug 2024 - Extract the userChallengeDimSelfEvalText if a matching evaluation is found
                                      const userEvalTextUpdateDate = matchingEval?.userChallengeEvals?.find(evaluation => evaluation.userChallengeDimId === dimension.challengeDimId._id)?.userChallengeDimSelfEvalTextUpdateDate
                                      || "N/A";

                                      // 31 Aug 2024 - Extract the matchingUser info
                                      // const userInfo = matchingEval?.userId
                                      // console.log('Matching User', userInfo)
                                      // console.log('SelfUserData', selfUserData)

                                      // 24 Aug 2024 - Filter the coach evaluations for the current dimension
                                        const matchingCoachEvals = filteredUserChallengeCoachEvals?.filter(evalItem =>
                                          evalItem.userChallengeCoachEvals?.some(evaluation => evaluation.userChallengeDimId === dimension.challengeDimId._id)
                                      );
                                      // console.log('matching coach evals:', matchingCoachEvals)

                                      // 25 Aug 2024 - Filter the sponsor evaluations for the current dimension
                                        const matchingSponsorEvals = filteredUserChallengeSponsorEvals?.filter(evalItem =>
                                          evalItem.userChallengeSponsorEvals?.some(evaluation => evaluation.userChallengeDimId === dimension.challengeDimId._id)
                                      );
                                      // console.log('matching sponsor evals:', matchingSponsorEvals)

                                      // 28 Aug 2024 Calculate if there are any self evaluations
                                      const hasUserEvals = filteredUserChallengeSelfEvals?.some(evalItem =>
                                        evalItem.userChallengeEvals?.some(evaluation =>
                                          dimensions.some(dimension =>
                                            evaluation.userChallengeDimId === dimension.challengeDimId._id
                                          )
                                        )
                                      );
                                      // console.log('hasUserEvals:', hasUserEvals)

                                    return(
                                          <div>
                                          {/* 24 Aug 2024 - Header Bar */}
                              
                                          <div key={dimension._id + dimIndex} className='bg-gray-100 m-1 p-1 rounded'>
                                              <div className='grid grid-cols-12 justify-between items-center'>
                                                  <div className='col-span-4 flex justify-start items-center m-1 p-1'>
                                                  <div
                                                      onClick={() =>
                                                        handleToggleChallengeDimEvalDetails(
                                                          dimension._id
                                                        )
                                                      }
                                                      className="cursor-pointer m-1 p-1"
                                                      >
                                                        {showChallengeDimEvalDetails[
                                                          dimension._id
                                                        ] ? (
                                                          <MinusIcon className="h-4 w-4 text-orange-600" />
                                                        ) : (
                                                          <PlusIcon className="h-4 w-4 text-orange-600" />
                                                        )}
                                                      </div>
                                                    <div>
                                                      <p className='text-4xl text-gray-400 font-bold mx-1 px-1'>{dimIndex + 1}.</p>
                                                    </div>
                                                    <div>
                                                      <div><p className='text-[10px] mx-1 px-1'><span className='font-bold'>
                                                        {dimension.challengeDimId?.challengeDimensionName}</span></p></div>
                                                      <div><p className='text-[10px] mx-1 px-1 italic'>{dimension.challengeDimId?.challengeDimensionDesc}</p></div>
                                                    </div>
                                                  </div>
                                                  {/* Dimension Evaluations */}
                                                 
                                                  <div className='col-span-8 gap-2 flex justify-end items-center'>
                                                  {/* 28 Aug 2024 */}
                                                  {hasUserEvals && displayUserEvalScore === "N/A" ? (
                                                      <div className="h-16 w-16 flex justify-center items-center border border-gray-400 rounded my-1 py-1 bg-gradient-to-r from-gray-800 to-gray-800 via-black">
                                                        <p className="text-gray-500 text-[9px]">No Eval</p>
                                                      </div>
                                                    ) : (
                                                      <div className={`flex justify-center items-center ${getColorForScore(displayUserEvalScore)} rounded h-16 w-16 border border-gray-400 my-1 py-1`}>
                                                        <p className='text-white'>{displayUserEvalScore}</p>
                                                      </div>
                                                    )}
                                                   
                                                    {/* Iterate over each coach evaluation or show "No Eval" if missing */}
                                                    {memoizedEvaluations[challenge._id]?.[headName]?.coaches.map(({ coach, matchingCoachEval }, coachIndex) => {
                                                    const coachEvalScore = matchingCoachEval?.userChallengeCoachEvals?.find(
                                                      evaluation => evaluation.userChallengeDimId === dimension.challengeDimId._id
                                                    )?.userChallengeDimCoachScore;

                                                    const displayCoachEvalScore = (coachEvalScore !== undefined && coachEvalScore !== null) ? coachEvalScore : "N/A";

                                                    if (shouldSkipDimension(dimension, matchingCoachEval, dimensions, coach, 'coach')) {
                                                      return null;
                                                    }

                                                  return (
                                                    <div key={coach._id + coachIndex} className='flex flex-col justify-center items-center'>
                                                      <div key={dimension.challengeDimId._id + dimIndex} className='flex flex-col justify-center items-center m-1 p-1'>
                                                        {displayCoachEvalScore === "N/A" ? (
                                                          <div className="h-16 w-16 flex justify-center items-center border border-gray-400 rounded my-1 py-1 bg-gradient-to-r from-gray-800 to-gray-800 via-black">
                                                            <p className="text-gray-500 text-[9px]">No Eval</p>
                                                          </div>
                                                        ) : (
                                                          <div className={`flex justify-center items-center ${getColorForScore(displayCoachEvalScore)} rounded h-16 w-16 border border-gray-400 my-1 py-1`}>
                                                            <p className='text-white'>{displayCoachEvalScore}</p>
                                                          </div>
                                                        )}
                                                      </div>
                                                    </div>
                                                  );
                                                })}

                                                {memoizedEvaluations[challenge._id]?.[headName]?.sponsors.map(({ sponsor, matchingSponsorEval }, sponsorIndex) => {
                                                  const sponsorEvalScore = matchingSponsorEval?.userChallengeSponsorEvals?.find(
                                                    evaluation => evaluation.userChallengeDimId === dimension.challengeDimId._id
                                                  )?.userChallengeDimSponsorScore;

                                                  const displaySponsorEvalScore = (sponsorEvalScore !== undefined && sponsorEvalScore !== null) ? sponsorEvalScore : "N/A";

                                                  if (shouldSkipDimension(dimension, matchingSponsorEval, dimensions, sponsor, 'sponsor')) {
                                                    return null;
                                                  }

                                                  return (
                                                    <div key={sponsor._id + sponsorIndex} className='flex flex-col justify-center items-center'>
                                                      <div key={dimension.challengeDimId._id + dimIndex} className='flex flex-col justify-center items-center m-1 p-1'>
                                                        {displaySponsorEvalScore === "N/A" ? (
                                                          <div className="h-16 w-16 flex justify-center items-center border border-gray-400 rounded my-1 py-1 bg-gradient-to-r from-gray-800 to-gray-800 via-black">
                                                            <p className="text-gray-500 text-[9px]">No Eval</p>
                                                          </div>
                                                        ) : (
                                                          <div className={`flex justify-center items-center ${getColorForScore(displaySponsorEvalScore)} rounded h-16 w-16 border border-gray-400 my-1 py-1`}>
                                                            <p className='text-white'>{displaySponsorEvalScore}</p>
                                                          </div>
                                                        )}
                                                      </div>
                                                    </div>
                                                  );
                                                })}
                                              </div>
                                          </div>
                                          {/* 30 Aug 2024 - Challenge Dim Eval Section */}
                                          {showChallengeDimEvalDetails[dimension._id] && (
                                            <div className='bg-white m-1 p-1'>
                                              <div><p className='m-1 p-1 font-bold text-gray-800'>Eval Comments</p></div>
                                              {/* 31 Aug 2024 - Grid for displaying each user, coach and sponsor eval comment for this dimension */}

                                              <div className='grid grid-cols-9 xl:grid-cols-10 gap-y-2 justify-start items-center bg-gray-50 my-2 px-1 py-2 rounded'>

                                                {/* 31 Aug 2024 - Put the user comments here for each eval dimension */}
                                                <div className='col-span-3 xl:col-span-2 m-1 p-1 bg-white h-full'>
                                                  {hasUserEvals && userEvalScore !== 'N/A' ?(
                                                    renderUserEvalInfo(selfUserData, "You", userEvalText, userEvalTextUpdateDate )
                                                  ):(
                                                    renderUserEvalInfo(selfUserData, "You", "You have not evaluated this dimension yet.", "No update date", "text-orange-600")
                                                  )}
                                                </div>

                                                {/* 31 Aug 2024 - Put each coach comment here */}
                                                
                                                {memoizedEvaluations[challenge._id]?.[headName]?.coaches?.map(({ coach, matchingCoachEval }, coachIndex) => {

                                                  const coachEvalScore = matchingCoachEval?.userChallengeCoachEvals?.find(
                                                    evaluation => evaluation.userChallengeDimId === dimension.challengeDimId._id
                                                  )?.userChallengeDimCoachScore;

                                                  const displayCoachEvalScore = (coachEvalScore !== undefined && coachEvalScore !== null) ? coachEvalScore : "N/A";

                                                  const coachEvalText = matchingCoachEval?.userChallengeCoachEvals?.find(
                                                    evaluation => evaluation.userChallengeDimId === dimension.challengeDimId._id
                                                  )?.userChallengeDimCoachEvalText;

                                                  const coachEvalTextUpdateDate = matchingCoachEval?.userChallengeCoachEvals?.find(
                                                    evaluation => evaluation.userChallengeDimId === dimension.challengeDimId._id
                                                  )?.userChallengeDimCoachEvalTextUpdateDate || 'Not updated';

                                                  return(
                                                    <div key={challenge._id+headName+coach._id+dimension._id+coachIndex} className='col-span-3 xl:col-span-2 m-1 p-1 bg-white h-full'>
                                                      {displayCoachEvalScore && displayCoachEvalScore !== 'N/A' ? (renderUserEvalInfo(coach, "Coach", coachEvalText, coachEvalTextUpdateDate))
                                                      : (
                                                        renderUserEvalInfo(coach, "Coach", "Coach has not evaluated this dimension yet.", "No update date", "text-orange-600")
                                                      )}
                                                    </div>
                                                  )
                                                })}
                                                
                                                {/* 31 Aug 2024 - Put each sponsor comment here */}

                                                {memoizedEvaluations[challenge._id]?.[headName]?.sponsors?.map(({ sponsor,     matchingSponsorEval }, sponsorIndex) => {

                                                  const sponsorEvalScore = matchingSponsorEval?.userChallengeSponsorEvals?.find(
                                                    evaluation => evaluation.userChallengeDimId === dimension.challengeDimId._id
                                                  )?.userChallengeDimSponsorScore;

                                                  const displaySponsorEvalScore = (sponsorEvalScore !== undefined && sponsorEvalScore !== null) ? sponsorEvalScore : "N/A";

                                                  const sponsorEvalText = matchingSponsorEval?.userChallengeSponsorEvals?.find(
                                                    evaluation => evaluation.userChallengeDimId === dimension.challengeDimId._id
                                                  )?.userChallengeDimSponsorEvalText;

                                                  const sponsorEvalTextUpdateDate = matchingSponsorEval?.userChallengeSponsorEvals?.find(
                                                    evaluation => evaluation.userChallengeDimId === dimension.challengeDimId._id
                                                  )?.userChallengeDimSponsorEvalTextUpdateDate || 'Not updated';

                                                  return(
                                                    <div key={challenge._id+headName+sponsor._id+dimension._id+sponsorIndex} className='col-span-3 xl:col-span-2 m-1 p-1 bg-white h-full'>
                                                      {displaySponsorEvalScore && displaySponsorEvalScore !== 'N/A' ? (renderUserEvalInfo(sponsor, "Sponsor", sponsorEvalText, sponsorEvalTextUpdateDate))
                                                      : (
                                                        renderUserEvalInfo(sponsor, "Sponsor", "Sponsor has not evaluated this dimension yet.", "No update date", "text-orange-600")
                                                      )}
                                                    </div>
                                                  )
                                                })}
                                              </div>
                                            </div>
                                          )}
                                      </div>
                                      </div>
                                  )})}
                              </div>
                            )})}
                        </div>  
                      </div>
                    )}  
                </div>
                    <EtModalAddOrUpdateUserChallengeSelfEval 
                      isOpen={isModalOpen} 
                      onClose={handleCloseChallengeEvalModal}
                      challengeId={challenge._id} 
                      userId={userId} 
                      userOrgId={userOrgId} 
                      trailId={trailId} 
                      sessionId={sessionId} 

                      // 04 Jul 2026 - Persist evaluation against active cohort.
                      cohortId={cohortId}


                      triggeringUrl={triggeringUrl} 
                      mode={mode} 
                      existingEvalData={existingEvalData}
                    />
                      {/* <EtModalCreateUserChallengeSelfEval isOpen={isModalOpen} onClose={handleCloseChallengeEvalModal} challengeId={challenge._id} userId={userId} userOrgId={userOrgId} trailId={trailId} sessionId={sessionId} triggeringUrl={triggeringUrl}/> */}
                    </div>
                </div>
              )}
          </div>
        )
           })}
          </div>
        )}
      </div>)}
    </div>
  )
}

const User = ({ username, defaultUserPic }) => {
  const { data: userPic, isFetching: isFetchingUserPic } =
    useGetUserPicQuery(username)

  return (
    <div className="flex justify-center items-center h-12 w-12 rounded-full border border-white bg-gradient-to-r from-orange-600 to-orange-400 shadow-lg shadow-black cursor-pointer">
      {/* Display user picture or loading indicator*/}
      {isFetchingUserPic ? (
        <div>Loading...</div>
      ) : userPic ? (
        <img src={userPic} alt="User" className="h-11 w-11  rounded-full" />
      ) : (
        <img src={defaultUserPic} alt="User" className="h-10 w-10 p-1" />
      )}
    </div>
  )
}

// 30 Aug 2024 - Helper function to render user info and evaluation comments - updated - 31 Aug 2024 - added userRelation
const renderUserEvalInfo = (user, userRelation, userEvalText, evalUpdateDate, textColor = "text-gray-800") => (
  <div className="flex flex-col items-start justify-start mx-1 px-1">
      <div className="flex justify-start items-center w-full">
          <div className="flex justify-start items-center m-1 p-1 w-16">
              {/* User Info */}
              <div className="flex flex-col justify-center items-center">
                  <div className="w-16">
                      <p className="text-orange-600 text-[9px] text-center">
                          {userRelation}
                      </p>
                  </div>
                  <div className="mx-1 px-1">
                      <User
                          username={user.username || user.userId?.username}
                          defaultUserPic={userProfileIcon2}
                      />
                  </div>
                  <div className="w-16 my-1 py-1">
                      <p className="text-gray-600 text-[10px] text-center">
                          {user?.name.split(" ")[0] || user.userId?.name.split(" ")[0]}
                      </p>
                  </div>
              </div>
          </div>
          <div className="m-1 p-1 border-l-2 border-orange-600">
              <div>
                  <p className="mx-1 text-gray-600 font-bold text-[10px]">
                      Comments
                  </p>
              </div>
              <div>
                  <p className={`mx-1 ${textColor} text-[10px]`}>
                      {userEvalText}
                  </p>
              </div>
          </div>
      </div>

      <div className="m-1 p-1">
          <div className="flex justify-start items-center">
              <ClockIcon className="h-5 w-5 text-lime-600" />
              <p className="mx-1 px-1 text-gray-500 text-[10px] font-bold">
                  Last Updated
              </p>
              <p className="text-[9px] mx-1 italic px-2 py-1 text-gray-400">
                  {getTimeSince(evalUpdateDate)}
              </p>
          </div>
      </div>
  </div>
);

export default EtUserTrailSessionIntrospectionCompProd